// ─────────────────────────────────────────────────────────────────────
// Tenon · TaskDetailPeek — kind-aware detail panel (view OR edit).
//
// One peek for four kinds (Task / Milestone / Phase / Decision) and
// two roles (canEdit = true / false). Decisions can carry a linked
// Finish Schedule spec, in which case the FS spec block renders inline
// (read+edit both surfaces from one place — bidirectional bridge).
//
// Port of tenon-ds/patterns/task-detail-peek.jsx → TypeScript.
// Design spec: ~/Desktop/Tenon/Design/task side peek update/README.md
//              JSX wins on any disagreement.
//
// Pickers live in task-detail-pickers.tsx + task-detail-pickers-extra.tsx
// ─────────────────────────────────────────────────────────────────────

import React from 'react'
import { useRefList } from '@/hooks/useRefList'
import { formatDateWithOptions } from '../../utils/dateUtils'
import { Icon, DropdownMenu } from '@/design-system/primitives'
import { appTasks, appPhases, authedFetch, estimates, estimateLineItems, purchaseOrders, bidRequests } from '@/lib/api'
import type { AppTask, TaskComment, TaskFlag, PurchaseOrder, BidRequest } from '@/lib/api'
import { usePermissions } from '@/lib/usePermissions'
import { poLabel, bidLabel, subLabel, atRiskReasonLabel } from '@/lib/procurement-status'
import {
  ProcurementStagePill,
  PROCUREMENT_STATUS_LABEL,
  STOCK_STATUS_CONFIG,
  type StockStatus,
} from '@/components/organisms/TaskMaterialsSection'
import { MaterialStatusPill } from '@/components/molecules/MaterialStatusPill'
import type { MaterialStatus } from '@/lib/scheduleTypes'
import { attachmentThumbUrl, attachmentUrl, isImageFile } from '@/components/task/shared/attachments'
import { AttachmentViewer, type ViewerAttachment } from '@/components/task/shared/AttachmentViewer'
import { CommentPanel } from '@/components/organisms/CommentPanel'
import { SidePeek } from './side-peek'
import { SubConfirmationsSection } from '@/components/organisms/SubConfirmations'
import { RelatedWorkSection, openRelatedCardPage, type RelatedWorkCard } from './related-work'
import { usePeekEscLayer } from '@/hooks/usePeekEscLayer'
import { FlagDialog, TaskFlagBanner, type FlagPersonOption, type FlagSubmitPayload } from './task-flag'
import type {
  PhaseOption,
  AssigneeOption,
  EquipmentOption,
  GoalOption,
  TaskTypeOption,
} from './task-detail-pickers'
import {
  PriorityPicker,
  StatusPicker,
  KindPicker,
  TaskTypePicker,
  PeekAssigneePicker,
  ProjectPicker,
  PhasePicker,
  ParentTaskPicker,
  EquipmentPicker,
  RepeatPicker,
  GoalPicker,
  BillablePicker,
  PeekDatePicker,
  DateTimePicker,
  DurationInput,
  TextStatic,
} from './task-detail-pickers'
import {
  getActiveWorkdayCalendar,
  setActiveWorkdayCalendar,
  addWorkingMinutes,
  subtractWorkingMinutes,
  diffWorkingMinutes,
  parseWorkdayDuration,
  formatWorkdayDuration,
  parseDatetimeLocal,
} from '@/lib/workday'
import { scheduleCalendars } from '@/lib/api'
import type {
  LocationOption,
  DepItem,
  Dependency,
  SpecOption,
  DecisionOption,
  EstimateLineItemOption,
} from './task-detail-pickers-extra'
import {
  MilestoneStatusPicker,
  DecisionStatusPicker,
  DeciderPicker,
  DependencyPicker,
  LinkedSpecPicker,
  FSBridgeBlock,
  DecisionOptionsEditor,
  SubKindPicker,
  SelectionModePicker,
  CloseModePicker,
  FromSpecMaterialRow,
  LocationRoomPicker,
  RoomSpecPill,
  locPath,
  type FSRoomOption,
  LinkedEstimateLineItemPicker,
} from './task-detail-pickers-extra'

// ─── Stacked linked-doc peeks (UOW-1A #3586) ─────────────────────────
// Sourcing chips open the linked PO / bid request in its real doc peek,
// stacked over this task peek (same layering the Procurement Tracker uses
// when it opens THIS peek over a tracker item). Lazily imported so the
// procurement chunk never loads until a chip is clicked, and so the
// design-system pattern can't form a static import cycle with
// components/procurement (§tdz class). The .catch fallback renders
// nothing when the chunk fails to load — the known stale-chunk-after-
// deploy class must degrade to hidden, never throw during render and
// take the whole page down (facet rule: never block the peek).
const POPeekLazy = React.lazy(() =>
  import('@/components/procurement/POPeek')
    .then(m => ({ default: m.POPeek }))
    .catch(() => ({ default: (() => null) as unknown as typeof import('@/components/procurement/POPeek').POPeek })))
const BidPeekLazy = React.lazy(() =>
  import('@/components/procurement/BidPeek')
    .then(m => ({ default: m.BidPeek }))
    .catch(() => ({ default: (() => null) as unknown as typeof import('@/components/procurement/BidPeek').BidPeek })))
// UOW-4G (#3672): the related-work strip's subcontract cards carry the
// public_id the /subcontracts-v2 routes resolve, so subs now stack their real
// peek here too instead of deep-linking away (SubPeek.tsx exists since #3587).
const SubPeekLazy = React.lazy(() =>
  import('@/components/procurement/SubPeek')
    .then(m => ({ default: m.SubPeek }))
    .catch(() => ({ default: (() => null) as unknown as typeof import('@/components/procurement/SubPeek').SubPeek })))

type LinkedDocPeekState =
  | { kind: 'po'; po: PurchaseOrder }
  | { kind: 'bid'; bid: BidRequest }
  | { kind: 'sub'; sub: import('@/components/procurement/SubPeek').SubcontractV2; changes: import('@/components/procurement/SubPeek').SubcontractChange[]; loadingChanges: boolean }

// ─── Types ────────────────────────────────────────────────────────────

export type TaskPeekKind = 'task' | 'milestone' | 'phase' | 'decision'

export interface PeekAttachment {
  id: string
  filename: string
  url: string
  thumbnail_url?: string
  size?: number
  mime_type?: string
  created_at?: string
}

/** Full data shape passed in via the `task` prop */
export interface PeekTask {
  id?: string
  name?: string
  kind?: TaskPeekKind
  status?: string
  priority?: string
  // Who
  assignees?: string[]
  decider?: string | null
  // Where
  project?: string | null
  phase?: string | null
  room?: string | null
  fsRoom?: string | null
  // Display-name fallbacks: the task's own joined names, shown by the pickers
  // when the value doesn't match a (possibly unloaded) option list — so the
  // peek mirrors what the list row shows instead of going blank.
  projectName?: string | null
  roomName?: string | null
  fsRoomName?: string | null
  phaseName?: string | null
  assigneeNames?: string[]
  // When
  start?: string | null
  due?: string | null
  duration?: string
  dueBy?: string | null
  orderLead?: string | null
  milestoneDate?: string | null
  // Content
  notes?: string
  dependencies?: Dependency[]
  linkedSpec?: SpecOption | null
  /** Estimate line item this task/phase is linked to (#2936) — id only;
   *  the peek resolves the full row from the fetched project options. */
  estimateLineItemId?: string | null
  /** Task-level material readiness rollup (none/required/sourcing/ready) —
   *  auto-derived server-side from procurement stages. Shown as the
   *  MaterialStatusPill on the Sourcing facet header (UOW-1A #3586). */
  materialStatus?: string | null
  options?: DecisionOption[]
  // Details (task-only)
  parentTask?: string | null
  goal?: string
  repeat?: string
  billable?: string
  equipment?: string[]
  taskType?: string
  // Decision settings
  subKind?: string
  selectionMode?: string
  closeMode?: string
  hasPricing?: boolean
  // Files
  attachments?: PeekAttachment[]
  // Flag-a-problem (#802/#810) — current open flag, or null/undefined.
  flag?: TaskFlag | null
}

// ─── Canonical AppTask → PeekTask normalization (exported for page use) ─────

export const API_TO_PEEK_STATUS: Record<string, string> = {
  'Not Started': 'not_started', 'In Progress': 'in_progress',
  'Blocked': 'blocked', 'Completed': 'completed',
  'Cancelled': 'cancelled', 'On Hold': 'on_hold',
  'pending': 'pending', 'hit': 'hit', 'missed': 'missed',
  'draft': 'draft', 'pending_client': 'pending',
  'approved': 'approved', 'rejected': 'rejected', 'revised': 'revised',
}
export const PEEK_TO_API_STATUS: Record<string, string> = {
  'not_started': 'Not Started', 'in_progress': 'In Progress',
  'blocked': 'Blocked', 'completed': 'Completed',
  'cancelled': 'Cancelled', 'on_hold': 'On Hold',
}
export const API_TO_PEEK_PRIORITY: Record<string, string> = {
  'Urgent': 'urgent', 'High': 'high', 'Normal': 'normal', 'Low': 'low',
}
export const PEEK_TO_API_PRIORITY: Record<string, string> = {
  'urgent': 'Urgent', 'high': 'High', 'normal': 'Normal', 'low': 'Low',
}
export function mapScheduleTypeToKind(t?: string): TaskPeekKind {
  if (t === 'milestone') return 'milestone'
  if (t === 'phase')     return 'phase'
  if (t === 'decision')  return 'decision'
  return 'task'
}
export function normalizeAppTaskToPeekTask(task: AppTask & Pick<PeekTask, 'dependencies'>): PeekTask {
  return {
    id:       task.id,
    name:     task.name,
    kind:     mapScheduleTypeToKind(task.schedule_type),
    status:   API_TO_PEEK_STATUS[task.status ?? ''] ?? task.status?.toLowerCase().replace(/\s+/g, '_'),
    priority: API_TO_PEEK_PRIORITY[task.priority ?? ''] ?? task.priority?.toLowerCase(),
    assignees: [
      ...(task.assignee_ids ?? (task.assignee_id ? [task.assignee_id] : [])).map(String),
      ...((task as { contact_assignee_ids?: number[] }).contact_assignee_ids ?? []).map((id: number) => String(-id)),
    ],
    // Index-aligned with `assignees` above (users first, then contacts) so
    // the picker can pair a not-yet-loaded id with its name positionally —
    // previously this dropped contact names entirely, so a contact assignee
    // rendered with no fallback label at all while the roster was loading.
    assigneeNames: [
      ...(task.assignee_names ?? (task.assignee_name ? [task.assignee_name] : [])),
      ...(task.contact_assignee_names ?? []),
    ],
    project:  task.project_public_id ?? (task.project_id != null ? String(task.project_id) : null),
    projectName: task.project_name ?? null,
    room:       task.location_id != null ? String(task.location_id) : null,
    roomName:   task.location_name ?? null,
    fsRoom:     (task as AppTask).room_folder_id ?? null,
    fsRoomName: (task as AppTask).room_folder_name ?? null,
    notes:    task.notes,
    // Some hosts hydrate dependency rows separately and spread them onto the
    // raw AppTask before opening the canonical peek. Preserve that host data
    // when normalizing so the dependency section and its async refresh see it.
    dependencies: task.dependencies,
    start:    task.start_date ?? null,
    due:      task.due_date ?? null,
    duration: task.duration_minutes ? String(Math.round(task.duration_minutes / 60)) + 'h' : '',
    parentTask: task.parent_task_id ?? null,
    phase:    task.phase_id ?? null,
    taskType: task.type ?? undefined,
    flag:     task.flag ?? null,
    estimateLineItemId: task.estimate_line_item_id != null ? String(task.estimate_line_item_id) : null,
    materialStatus: task.material_status ?? null,
  }
}

export interface TaskDetailPeekProps {
  open: boolean
  onClose: () => void
  task?: PeekTask | AppTask | null
  kind?: TaskPeekKind
  canEdit?: boolean
  estimate?: boolean
  taskTypeEnabled?: boolean
  onOpenLinkedSpec?: (specId: string) => void
  /** TASK-DETAIL-PEEK-DEP-HOVER-PREVIEW — opens stacked peek for task deps. */
  onOpenTask?: (taskId: string) => void
  onBack?: () => void
  breadcrumb?: string
  onExpand?: () => void
  onKindChange?: (kind: TaskPeekKind) => void
  // Picker data
  phases?: PhaseOption[]
  locations?: LocationOption[]
  fsRoomOptions?: FSRoomOption[]
  projectOptions?: { id: string; name: string; locationId?: number | null }[]
  assigneeOptions?: AssigneeOption[]
  goalOptions?: GoalOption[]
  equipmentOptions?: EquipmentOption[]
  taskTypeOptions?: TaskTypeOption[]
  depItems?: DepItem[]
  /** Tasks that depend ON this one (successors) — the peek only shows what
   *  this task waits ON by default; this renders a "Blocking" line under
   *  Dependencies so the cost of slipping this task is visible where the
   *  date is edited (SCHED-W4 §13). Click a row to open it (stacked peek). */
  blockingTasks?: { id: string; name: string; status?: string; _depId?: number }[]
  /** Removes a dependency row (either side — a predecessor in Dependencies
   *  or a successor in Blocking) by its own app_task_dependencies id. See #2798. */
  onRemoveDependency?: (depId: number) => void
  specOptions?: SpecOption[]
  /** Candidate parent tasks for the Parent-task picker. Without this the
   *  picker renders an empty "No parent" list and reads as broken. */
  parentTaskOptions?: { id: string; name: string; status?: string }[]
  /** Direct children of this task. When non-empty, the peek renders a
   *  "Subtasks" section with one card per child (click → onOpenTask). */
  childTasks?: { id: string; name: string; status?: string; assignee_name?: string | null; due_date?: string | null }[]
  /** Open a child/subtask in a stacked peek. */
  onOpenChildTask?: (taskId: string) => void
  // Callbacks
  onFieldSave?: (field: string, value: unknown) => void | Promise<void>
  /** UOW-4F (#3671): fired after the peek changes the task↔estimate-line
   *  GRAPH — removing a line from a combined task can create a brand-new task
   *  (the split), which the schedule behind the peek has to refetch to show.
   *  Deliberately NOT routed through onFieldSave: a PATCH of
   *  estimate_line_item_id REPLACES a task's links, which would destroy the
   *  other lines of a combined task. */
  onEstimateLinksChanged?: () => void
  /** localId is a client-generated id for this upload; callers with no server
   *  round trip yet (e.g. create mode, task doesn't exist) echo it back as the
   *  attachment id so the optimistic preview below can be removed by it. */
  onFileUpload?: (file: File, localId: string) => Promise<void>
  onFileRemove?: (attachmentId: string) => Promise<void>
  onDuplicate?: () => void
  onDelete?: () => void
  /**
   * Fine-grained field-level edit gate.  Works in two modes:
   *
   * (A) Global-edit mode (`canEdit=true`): return `false` for a `fieldKey` to
   *     disable a specific action. Supported keys in this mode:
   *       'status_approve' — disables the Approved transition in DecisionStatusPicker
   *       'status_reject'  — disables the Rejected transition in DecisionStatusPicker
   *
   * (B) Kiosk/allowlist mode (`canEdit=false`): return `true` for a `fieldKey`
   *     to enable that specific field while all others remain read-only.
   *     Supported keys in this mode:
   *       'status' — makes the Status pill writable
   *       'notes'  — makes the Notes textarea writable
   *     Example (location kiosk — operators mark Complete + leave a note):
   *       canEditField={(key) => key === 'status' || key === 'notes'}
   *
   * Absent (undefined) ⇒ `canEdit` governs all fields uniformly.
   */
  canEditField?: (fieldKey: string) => boolean
  /** Rendered in a sticky footer bar at the bottom of the peek panel.
   *  Use for primary actions in draft/create flows (e.g. "Create item"). */
  footer?: React.ReactNode
  /**
   * 'client_review' — read-only mode for client portal; options show a
   * "Pick this" CTA instead of the internal options editor.
   */
  mode?: 'normal' | 'client_review'
  /** Called when client selects an option in 'client_review' mode. */
  onOptionSelect?: (optionId: string) => void | Promise<void>
  /**
   * Mobile fullscreen mode — renders as a fixed fullscreen overlay that slides
   * up from the bottom instead of a 440px side panel. Use on mobile surfaces
   * (< 768px viewports). Respects iOS safe-area-inset-bottom.
   */
  mobile?: boolean
  /**
   * Panel z-index (UOW-4G stacking ladder). SidePeek's default is 200 —
   * below every SlideOver (901) — which is right when THIS peek is the base
   * layer and doc peeks stack over it. Hosts that open this peek OVER a
   * SlideOver-based peek (a related-work task card in the PO/Bid/Sub or
   * estimate-line peek) pass their own z + 10 so the task peek lands on
   * top; the doc peeks this peek then opens go another step above.
   */
  zIndex?: number
}

const EMPTY_DEP_ITEMS: DepItem[] = []

// ─── Kind catalogues ─────────────────────────────────────────────────

const SCHEDULE_KINDS = [
  { id: 'task',      label: 'Task',      dot: 'var(--text-muted)' },
  { id: 'milestone', label: 'Milestone', dot: 'var(--brand-gold)' },
  { id: 'decision',  label: 'Decision',  dot: 'var(--st-warning, #E89B3C)' },
  { id: 'phase',     label: 'Phase',     dot: 'var(--status-in-progress-dot)' },
]

const ESTIMATE_KINDS = [
  { id: 'subcontractor', label: 'Subcontractor', dot: 'var(--status-completed-dot)' },
  { id: 'material',      label: 'Material',      dot: 'var(--status-on-hold-dot)' },
  { id: 'fee',           label: 'Fee',           dot: 'var(--status-blocked-dot)' },
  { id: 'labour',        label: 'Labour',        dot: 'var(--status-in-progress-dot)' },
  { id: 'equipment',     label: 'Equipment',     dot: 'var(--st-warning)' },
  { id: 'allowance',     label: 'Allowance',     dot: 'var(--brand-gold)' },
  { id: 'assembly',      label: 'Assembly',      dot: 'var(--brand-gold)' },
]

// ─── Shared style constants ───────────────────────────────────────────

const sectStyle: React.CSSProperties = {
  padding: '14px 18px',
  borderBottom: '1px solid var(--border)',
}

const iconBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, borderRadius: 'var(--r-sm)',
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: 'var(--text-muted)', flexShrink: 0,
}

// ─── Internal helpers ─────────────────────────────────────────────────

function SectionLabel({ children, action = null, style = {} }: {
  children: React.ReactNode
  action?: React.ReactNode
  style?: React.CSSProperties
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline',
      justifyContent: 'space-between',
      marginBottom: 8,
      ...style,
    }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
        letterSpacing: 0.8, textTransform: 'uppercase' as const,
        color: 'var(--text-muted)',
      }}>{children}</div>
      {action}
    </div>
  )
}

function FieldLabel({ children, required = false }: {
  children: React.ReactNode
  required?: boolean
}) {
  return (
    <div style={{
      fontFamily: 'var(--font-sans)', fontSize: 10.5, fontWeight: 700,
      letterSpacing: 0.7, textTransform: 'uppercase' as const,
      color: 'var(--text-muted)', marginBottom: 5,
    }}>
      {children}
      {required && <span style={{ color: 'var(--st-error)', marginLeft: 2 }}>*</span>}
    </div>
  )
}

function PeekField({ label, required, children, span = 12, style = {} }: {
  label?: string
  required?: boolean
  children: React.ReactNode
  span?: number
  style?: React.CSSProperties
}) {
  return (
    <div style={{ gridColumn: `span ${span}`, marginBottom: 0, ...style }}>
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      {children}
    </div>
  )
}

function SectionGrid({ children, cols = 2, gap = 12, style = {} }: {
  children: React.ReactNode
  cols?: number
  gap?: number
  style?: React.CSSProperties
}) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${cols * 6}, 1fr)`,
      gap, ...style,
    }}>{children}</div>
  )
}

function Divider() {
  return <div style={{ height: 1, background: 'var(--border)', margin: '14px 0' }} />
}

// ─── Compact hover-to-edit property row (Linear/Height density) ───────
// Matches task-peek-web.jsx `Prop`: 130px label column + 1fr value column,
// no border/background box, row-hover highlight, 34px row height.
// Replaces the old FieldLabel-above-40px-bordered-cell pattern for the
// Who/Where/When/Details property list (task-peek-web.jsx §PEEK-REDESIGN-01).

function CompactRow({ icon, label, children }: {
  icon?: string
  label: string
  children: React.ReactNode
}) {
  const [hover, setHover] = React.useState(false)
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid', gridTemplateColumns: '130px 1fr',
        alignItems: 'center', gap: 6,
        minHeight: 34, padding: '1px 8px', margin: '0 -8px',
        borderRadius: 7,
        background: hover ? 'var(--row-hover)' : 'transparent',
        transition: 'background var(--dur-fast) var(--ease-out)',
      }}>
      <span style={{
        display: 'flex', alignItems: 'center', gap: 7,
        fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 500,
        color: 'var(--text-muted)',
      }}>
        {icon && <Icon name={icon} size={13} sw={1.8} style={{ color: 'var(--text-dim)' }} />}
        {label}
      </span>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  )
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: '14px 8px 5px',
      fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
      letterSpacing: 0.8, textTransform: 'uppercase' as const,
      color: 'var(--text-muted)',
    }}>{children}</div>
  )
}

// ─── Header pill row ──────────────────────────────────────────────────

function shortDate(v?: string | null): string {
  if (!v) return ''
  const d = /^\d{4}-\d{2}-\d{2}/.test(v)
    ? new Date(v.slice(0, 10) + 'T00:00:00')
    : new Date(v)
  if (isNaN(d.getTime())) return v
  return formatDateWithOptions(d, { month: 'short', day: 'numeric' })
}

function DueChip({ start, due, overdue = false }: {
  start?: string | null
  due?: string | null
  overdue?: boolean
}) {
  const label = !due
    ? null
    : (start && start !== due)
      ? `${shortDate(start)} → ${shortDate(due)}`
      : `Due ${shortDate(due)}`
  if (!label) return null
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 8px',
      background: overdue ? 'color-mix(in srgb, var(--st-error) 12%, transparent)' : 'transparent',
      color: overdue ? 'var(--st-error)' : 'var(--text-muted)',
      borderRadius: 999,
      fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
      letterSpacing: 0.4,
    }}>
      <Icon name="calendar" size={11} sw={2} />
      {label}
    </span>
  )
}

// ─── TitleBlock ───────────────────────────────────────────────────────

function TitleBlock({
  kind, kindList, name, priority, status, start, due,
  notes, onNotesChange, notesEditable,
  onKindChange, onNameChange, onPriorityChange, onStatusChange,
  canEdit, canEditField, canEditStatus, flagBanner, taskId,
}: {
  kind: string
  kindList: typeof SCHEDULE_KINDS
  name?: string
  priority?: string
  status?: string
  start?: string | null
  due?: string | null
  notes?: string
  onNotesChange?: (v: string) => void
  /** Independent edit gate for the description field (kiosk allowlist mode). */
  notesEditable?: boolean
  onKindChange?: (v: string) => void
  onNameChange?: (v: string) => void
  onPriorityChange?: (v: string) => void
  onStatusChange?: (v: string) => void
  canEdit: boolean
  canEditField?: (fieldKey: string) => boolean
  /** Override status-picker editability independently of canEdit.
   *  Used by kiosk allowlist mode — canEdit=false but status is writable. */
  canEditStatus?: boolean
  /** Rendered directly under the description, inside this same bordered
   *  block (task-peek-web.jsx's flagged-banner placement) — TaskFlagBanner
   *  when the task carries an open flag, or omitted otherwise. */
  flagBanner?: React.ReactNode
  /** Keys the title textarea so it remounts on a genuine task switch (fresh
   *  defaultValue, no leftover edits) but NOT on every keystroke/blur within
   *  the same task — see BUG-1440. */
  taskId?: string
}) {
  // canEditStatus overrides canEdit for the status pill when explicitly provided.
  const statusEditable = canEditStatus !== undefined ? canEditStatus : canEdit
  const showPriority = kind === 'task'
  const showDue = !!(due || start)
  const isOverdue = !!(due && new Date(due) < new Date() && kind !== 'phase')

  // For decisions: build a list of status ids the current role cannot transition to.
  const decisionDisabledStatuses: string[] = []
  if (kind === 'decision' && canEditField) {
    if (!canEditField('status_approve')) decisionDisabledStatuses.push('approved')
    if (!canEditField('status_reject'))  decisionDisabledStatuses.push('rejected')
  }

  return (
    <div style={{ padding: '8px 18px 16px', borderBottom: '1px solid var(--border)' }}>
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center' }}>
        <KindPicker list={kindList} kind={kind} value={kind}
          onChange={(v) => onKindChange?.(v)} canEdit={canEdit} />
      </div>
      {canEdit ? (
        <textarea
          defaultValue={name || ''}
          key={`title-${taskId ?? 'no-task'}`}
          onBlur={(e) => onNameChange?.(e.target.value)}
          placeholder="Title"
          rows={1}
          onInput={(e) => {
            const el = e.currentTarget
            el.style.height = 'auto'
            el.style.height = el.scrollHeight + 'px'
          }}
          style={{
            width: '100%', resize: 'none', overflow: 'hidden',
            background: 'transparent', border: 'none', outline: 'none',
            padding: '4px 0',
            fontFamily: 'var(--font-sans)', fontSize: 18, fontWeight: 700,
            letterSpacing: -0.3, lineHeight: 1.25,
            color: 'var(--text-primary)',
            boxSizing: 'border-box',
          }}
        />
      ) : (
        <h2 style={{
          margin: '4px 0',
          fontFamily: 'var(--font-sans)', fontSize: 18, fontWeight: 700,
          letterSpacing: -0.3, lineHeight: 1.25,
          color: 'var(--text-primary)',
        }}>
          {name || <span style={{ color: 'var(--text-dim)' }}>Untitled</span>}
        </h2>
      )}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
        marginTop: 10,
      }}>
        {showPriority && (
          <PriorityPicker value={priority || 'normal'} onChange={onPriorityChange} canEdit={canEdit} />
        )}
        {kind === 'milestone' ? (
          <MilestoneStatusPicker value={status || 'pending'} onChange={onStatusChange} canEdit={statusEditable} />
        ) : kind === 'decision' ? (
          <DecisionStatusPicker
            value={status || 'draft'}
            onChange={onStatusChange}
            canEdit={statusEditable}
            disabledIds={decisionDisabledStatuses}
          />
        ) : (
          <StatusPicker value={status || 'not_started'} onChange={onStatusChange} canEdit={statusEditable} />
        )}
        <div style={{ flex: 1 }} />
        {showDue && <DueChip start={start} due={due} overdue={isOverdue} />}
      </div>

      {/* Description — directly under the title, quiet inline field (task-peek-web.jsx).
          Replaces the old titled NOTES markdown-toolbar box. */}
      {(notesEditable ?? canEdit) ? (
        <textarea
          defaultValue={notes || ''}
          key={`notes-${taskId ?? 'no-task'}`}
          ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px' } }}
          onBlur={(e) => onNotesChange?.(e.target.value)}
          placeholder="Add a description — scope, context, or how to know it's done…"
          onInput={(e) => {
            const el = e.currentTarget
            el.style.height = 'auto'
            el.style.height = el.scrollHeight + 'px'
          }}
          style={{
            width: '100%', marginTop: 12, minHeight: 48,
            padding: '9px 11px', resize: 'none', overflow: 'hidden',
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)', outline: 'none',
            fontFamily: 'var(--font-sans)', fontSize: 13.5, lineHeight: 1.55,
            color: 'var(--text-primary)', boxSizing: 'border-box',
          }}
        />
      ) : (
        notes && (
          <p style={{
            margin: '12px 0 0',
            fontFamily: 'var(--font-sans)', fontSize: 13.5, lineHeight: 1.55,
            color: 'var(--text-secondary)',
          }}>{notes}</p>
        )
      )}
      {flagBanner}
    </div>
  )
}

// ─── Sections ─────────────────────────────────────────────────────────

// ─── PropertiesSection — merged Who/Where/When/Details ────────────────
// task-peek-web.jsx renders Assignees → Where → When → Details as ONE
// continuous compact-row list (single outer border, GroupLabel dividers —
// not four separately-bordered sections with a collapsed Details accordion).
// PEEK-REDESIGN-01 AC1/AC1(details-not-collapsed).

function PropertiesSection({
  kind, taskId,
  assignees, decider, assigneeOptions, assigneeNames,
  onAssigneesChange, onDeciderChange,
  project, phase, room, fsRoom, fsRoomName,
  projectName, roomName, phaseName,
  onProjectChange, onPhaseChange, onLocationRoomChange,
  phases, locations, fsRoomOptions, projectOptions,
  start, due, duration, dueBy, milestoneDate, orderLeadDate, hasLinkedSpec,
  onStartChange, onDueChange, onDurationChange, onDueByChange,
  onMilestoneDateChange, onOrderLeadChange,
  parentTask, goal, repeat, billable, equipment, taskType,
  onParentChange, onGoalChange, onRepeatChange, onBillableChange, onEquipmentChange, onTaskTypeChange,
  canEdit, canEditTaskType, taskTypeEnabled,
  goalOptions, equipmentOptions, taskTypeOptions, parentTaskOptions,
}: {
  kind: string
  taskId?: string
  assignees?: string[]
  decider?: string | null
  assigneeOptions?: AssigneeOption[]
  assigneeNames?: string[]
  onAssigneesChange?: (v: string[]) => void
  onDeciderChange?: (v: string | null) => void
  project?: string | null
  phase?: string | null
  room?: string | null
  fsRoom?: string | null
  fsRoomName?: string | null
  projectName?: string | null
  roomName?: string | null
  phaseName?: string | null
  onProjectChange?: (v: string | null) => void
  onPhaseChange?: (v: string | null) => void
  onLocationRoomChange?: (v: { locationId: string | null; roomId: string | null; roomName?: string | null }) => void
  phases?: PhaseOption[]
  locations?: LocationOption[]
  fsRoomOptions?: FSRoomOption[]
  projectOptions?: { id: string; name: string; locationId?: number | null }[]
  start?: string | null
  due?: string | null
  duration?: string
  dueBy?: string | null
  milestoneDate?: string | null
  orderLeadDate?: string | null
  hasLinkedSpec?: boolean
  onStartChange?: (v: string | null) => void
  onDueChange?: (v: string | null) => void
  onDurationChange?: (v: string) => void
  onDueByChange?: (v: string | null) => void
  onMilestoneDateChange?: (v: string | null) => void
  onOrderLeadChange?: (v: string | null) => void
  parentTask?: string | null
  goal?: string
  repeat?: string
  billable?: string
  equipment?: string[]
  taskType?: string
  onParentChange?: (v: string | null) => void
  onGoalChange?: (v: string) => void
  onRepeatChange?: (v: string) => void
  onBillableChange?: (v: string) => void
  onEquipmentChange?: (v: string[]) => void
  onTaskTypeChange?: (v: string) => void
  canEdit: boolean
  canEditTaskType: boolean
  taskTypeEnabled: boolean
  goalOptions?: GoalOption[]
  equipmentOptions?: EquipmentOption[]
  taskTypeOptions?: TaskTypeOption[]
  parentTaskOptions?: { id: string; name: string; status?: string }[]
}) {
  const wantsLocation = kind !== 'phase'
  const phaseLabel = kind === 'phase' ? 'Parent phase' : 'Phase'

  return (
    <section style={sectStyle}>
      {/* Who — no group label on web (matches task-peek-web.jsx: Assignees is the first row) */}
      {kind === 'decision' ? (
        <>
          <CompactRow icon="user" label="Decider">
            <DeciderPicker value={decider || null} onChange={onDeciderChange} canEdit={canEdit}
              options={assigneeOptions?.map(a => ({ id: a.id, name: a.name || a.id }))} compact />
          </CompactRow>
          <CompactRow icon="user" label="Watchers">
            <PeekAssigneePicker value={assignees || []} onChange={onAssigneesChange}
              canEdit={canEdit} options={assigneeOptions} projectId={project} compact
              taskId={taskId} projectName={projectName} />
          </CompactRow>
        </>
      ) : kind !== 'phase' && (
        <CompactRow icon="user" label="Assignees">
          <PeekAssigneePicker value={assignees || []} onChange={onAssigneesChange}
            canEdit={canEdit} options={assigneeOptions} currentNames={assigneeNames} projectId={project} compact
            taskId={taskId} projectName={projectName} />
        </CompactRow>
      )}

      {/* Where */}
      <GroupLabel>Where</GroupLabel>
      <CompactRow icon="briefcase" label="Project">
        <ProjectPicker value={project || undefined} onChange={onProjectChange}
          canEdit={canEdit} options={projectOptions || []} currentName={projectName} compact />
      </CompactRow>
      <CompactRow icon="layers" label={phaseLabel}>
        <PhasePicker value={phase || null} onChange={onPhaseChange}
          canEdit={canEdit} phases={phases || []} label={phaseLabel}
          projectId={project || null} currentName={phaseName} compact />
      </CompactRow>
      {wantsLocation && (
        <CompactRow icon="grid" label="Location">
          <LocationRoomPicker
            locationValue={room || null} roomValue={fsRoom || null}
            currentLocationName={roomName} currentRoomName={fsRoomName}
            onChange={onLocationRoomChange}
            canEdit={canEdit} locations={locations || []} rooms={fsRoomOptions || []}
            projectId={project || undefined} compact
            renderTrigger={(_label, placeholder) => {
              // Breadcrumb (parent muted, room bold) + finish pill + chevron —
              // mirrors LocationRoomPicker's own !canEdit static branch, which
              // ScheduleListView's List cell already reuses via the same prop
              // (SCHED-W4 §11 · #2430 AC1).
              const curRoom = (fsRoomOptions || []).find(r => r.folder_id === fsRoom) || null
              const roomLocId = curRoom?.location_id != null ? String(curRoom.location_id) : room
              const path = roomLocId ? locPath(roomLocId, locations || []).map(p => p.name) : (roomName ? [roomName] : [])
              const segs = [...path]
              const roomLabel = curRoom?.name ?? fsRoomName ?? null
              if (roomLabel && segs[segs.length - 1] !== roomLabel) segs.push(roomLabel)
              const deduped = segs.filter((s, i) => i === 0 || s !== segs[i - 1])
              return (
                <button type="button" style={{
                  all: 'unset', width: '100%', cursor: 'pointer', boxSizing: 'border-box',
                  display: 'flex', alignItems: 'center', gap: 5, minHeight: 30, padding: '0 6px',
                  fontFamily: 'var(--font-sans)', fontSize: 13,
                }}>
                  {deduped.length === 0 ? (
                    <span style={{ color: 'var(--text-dim)' }}>{placeholder ? 'No location' : '—'}</span>
                  ) : deduped.map((seg, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <Icon name="chevR" size={9} sw={2} style={{ color: 'var(--text-dim)', flexShrink: 0 }}/>}
                      <span style={{
                        color: i === deduped.length - 1 ? 'var(--text-primary)' : 'var(--text-muted)',
                        fontWeight: i === deduped.length - 1 ? 600 : 500,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{seg}</span>
                    </React.Fragment>
                  ))}
                  {curRoom && <RoomSpecPill room={curRoom}/>}
                  <span style={{ flex: 1 }}/>
                  <Icon name="chevD" size={12} sw={2} style={{ color: 'var(--text-dim)', flexShrink: 0 }}/>
                </button>
              )
            }}
          />
        </CompactRow>
      )}

      {/* When */}
      {kind !== 'phase' && (
        <>
          <GroupLabel>When</GroupLabel>
          {kind === 'milestone' && (
            <CompactRow icon="calendar" label="Date">
              <PeekDatePicker value={milestoneDate || null} onChange={onMilestoneDateChange}
                canEdit={canEdit} placeholder="Pick a date" compact />
            </CompactRow>
          )}
          {kind === 'decision' && (
            <>
              <CompactRow icon="calendar" label="Decision due by">
                <PeekDatePicker value={dueBy || null} onChange={onDueByChange}
                  canEdit={canEdit} placeholder="Pick a date" compact />
              </CompactRow>
              {hasLinkedSpec && (
                <CompactRow icon="calendar" label="Order lead date">
                  <PeekDatePicker value={orderLeadDate || null} onChange={onOrderLeadChange}
                    canEdit={canEdit} placeholder="Optional" compact />
                </CompactRow>
              )}
            </>
          )}
          {kind === 'task' && (
            <>
              <CompactRow icon="calendar" label="Start">
                <DateTimePicker value={start || null} onChange={onStartChange}
                  canEdit={canEdit} placeholder="Pick date & time" compact />
              </CompactRow>
              <CompactRow icon="calendar" label="Due">
                <DateTimePicker value={due || null} onChange={onDueChange}
                  canEdit={canEdit} placeholder="Pick date & time" compact />
              </CompactRow>
              <CompactRow icon="clock" label="Duration">
                {canEdit ? (
                  <DurationInput value={duration || ''} onChange={onDurationChange} canEdit={canEdit} compact />
                ) : (
                  <TextStatic value={duration} />
                )}
              </CompactRow>
            </>
          )}
        </>
      )}

      {/* Details — always expanded, never a collapsed accordion (AC1) */}
      {kind === 'task' && (
        <>
          <GroupLabel>Details</GroupLabel>
          {taskTypeEnabled && (taskTypeOptions ?? []).length > 0 && (
            <CompactRow icon="briefcase" label="Task type">
              <TaskTypePicker value={taskType || ''} onChange={onTaskTypeChange} canEdit={canEditTaskType} options={taskTypeOptions || []} compact />
            </CompactRow>
          )}
          <CompactRow icon="link" label="Parent task">
            <ParentTaskPicker value={parentTask || null} onChange={onParentChange} canEdit={canEdit} options={parentTaskOptions || []} projectId={project || null} compact />
          </CompactRow>
          <CompactRow icon="refresh" label="Repeat">
            <RepeatPicker value={repeat || 'none'} onChange={onRepeatChange} canEdit={canEdit} compact />
          </CompactRow>
          <CompactRow label="Goal">
            <GoalPicker value={goal || 'none'} onChange={onGoalChange}
              canEdit={canEdit} goals={goalOptions || []} compact />
          </CompactRow>
          <CompactRow label="Billable">
            <BillablePicker value={billable || 'billable'} onChange={onBillableChange} canEdit={canEdit} compact />
          </CompactRow>
          <CompactRow icon="briefcase" label="Equipment">
            <EquipmentPicker value={equipment || []} onChange={onEquipmentChange}
              canEdit={canEdit} options={equipmentOptions || []} compact />
          </CompactRow>
        </>
      )}
    </section>
  )
}

function DependenciesSection({
  deps, onDepsChange, canEdit, kind, onOpenSpec, onOpenTask, depItems, blockingTasks, onRemoveDependency,
}: {
  deps?: Dependency[]
  onDepsChange?: (v: Dependency[]) => void
  canEdit: boolean
  kind: string
  onOpenSpec?: (id: string) => void
  onOpenTask?: (id: string) => void
  depItems?: DepItem[]
  blockingTasks?: { id: string; name: string; status?: string; _depId?: number }[]
  onRemoveDependency?: (depId: number) => void
}) {
  return (
    <section style={sectStyle}>
      <SectionLabel>Dependencies</SectionLabel>
      <DependencyPicker value={deps || []} onChange={onDepsChange}
        canEdit={canEdit} onOpenSpec={onOpenSpec} onOpenTask={onOpenTask} depItems={depItems || []}
        onRemoveDependency={onRemoveDependency} />
      {canEdit && (
        <div style={{
          marginTop: 6, fontFamily: 'var(--font-sans)', fontSize: 11.5,
          color: 'var(--text-muted)',
        }}>
          Specs gate on approval. Tasks gate on completion (FS by default).
        </div>
      )}
      {blockingTasks && blockingTasks.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6,
            fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 700,
            letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-muted)',
          }}>
            <Icon name="arrowR" size={10} sw={2.2} style={{ color: 'var(--st-error)' }}/>
            Blocking · {blockingTasks.length} task{blockingTasks.length > 1 ? 's' : ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {blockingTasks.map(b => (
              <div key={b.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '6px 8px', borderRadius: 'var(--r-sm)',
                  border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                  fontFamily: 'var(--font-sans)', fontSize: 12.5,
                }}>
                <button type="button"
                  onClick={() => onOpenTask?.(b.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    flex: 1, minWidth: 0, textAlign: 'left',
                    background: 'transparent', border: 'none', padding: 0,
                    cursor: onOpenTask ? 'pointer' : 'default',
                    fontFamily: 'var(--font-sans)', fontSize: 12.5,
                  }}>
                  <span style={{
                    flex: 1, minWidth: 0, color: 'var(--text-primary)', fontWeight: 500,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{b.name}</span>
                  {onOpenTask && <Icon name="chevR" size={11} sw={2} style={{ color: 'var(--text-muted)', flexShrink: 0 }}/>}
                </button>
                {canEdit && onRemoveDependency && b._depId != null && (
                  <button type="button"
                    onClick={() => onRemoveDependency(b._depId as number)}
                    aria-label={`Remove blocking dependency on ${b.name}`}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 20, height: 20, flexShrink: 0,
                      background: 'transparent', border: 'none', borderRadius: 'var(--r-sm)',
                      cursor: 'pointer', color: 'var(--text-muted)',
                    }}>
                    <Icon name="x" size={10} sw={2.4}/>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

function FSBridgeSection({
  linkedSpec, onLinkedSpecChange, canEdit, specOptions,
}: {
  linkedSpec?: SpecOption | null
  onLinkedSpecChange?: (v: SpecOption | null) => void
  canEdit: boolean
  specOptions?: SpecOption[]
}) {
  return (
    <section style={sectStyle}>
      <SectionLabel
        action={
          linkedSpec && canEdit ? (
            <button type="button"
              onClick={() => onLinkedSpecChange?.(null)}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontFamily: 'var(--font-sans)', fontSize: 11.5, fontWeight: 600,
                color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
              <Icon name="x" size={10} sw={2} />
              Unlink
            </button>
          ) : null
        }>
        Linked spec
      </SectionLabel>
      {linkedSpec ? (
        <FSBridgeBlock spec={linkedSpec} onOpenInFS={() => {}} canEdit={false} />
      ) : (
        <LinkedSpecPicker value={null}
          onChange={(v) => onLinkedSpecChange?.(v as SpecOption | null)}
          canEdit={canEdit} specOptions={specOptions || []} />
      )}
      {canEdit && (
        <div style={{
          marginTop: 6, fontFamily: 'var(--font-sans)', fontSize: 11,
          color: 'var(--text-muted)',
        }}>
          When linked, edits to the spec sync back to Finish Schedule.
        </div>
      )}
    </section>
  )
}

// ─── Cost facet helpers (UOW-1A #3586) — exported for tests ───────────

/** Formats the live estimate line's markup for display. Estimate
 *  `markup_pct` is a FRACTION (0–1 usually; >1 is a legitimate >100%
 *  markup) — displayed ×100 as a percent. Flat markups show the amount. */
export function formatEstimateMarkup(opt: {
  markup_pct?: number
  markup_type?: 'percent' | 'flat'
  markup_flat_amount?: number
}): string | null {
  if (opt.markup_type === 'flat') {
    return opt.markup_flat_amount != null ? `${fmt$(opt.markup_flat_amount)} flat` : null
  }
  if (opt.markup_pct == null) return null
  const pct = Math.round(opt.markup_pct * 100 * 100) / 100
  return `${pct}%`
}

/**
 * Variance detector (contract §5): true when ANY task material row's
 * qty / unit cost differ from ITS OWN live estimate line — the line named
 * by the material's `estimate_line_item_id`, NOT the task's linked line.
 * The create-tasks fan-out links the TASK to an assembly's PARENT line
 * while its materials carry the CHILD line ids, so a task-line comparison
 * could never fire on the real path. Rules: any qty delta, or a strictly-
 * more-than-1¢ unit-cost delta. Markup is deliberately NOT compared:
 * task material rows never carry a real markup (writers leave the column
 * at its 0 default), so any markup term is a permanent false positive.
 * A material whose line id has no live line (deleted / not fetched) is
 * skipped — that's the dangling-provenance display case, not variance.
 * Never mutates anything — display only, real changes go through change
 * orders (locked: no silent repricing).
 */
export function materialsDifferFromEstimate(
  materials: {
    estimate_line_item_id?: number | null
    quantity?: number
    unit_cost?: number
  }[],
  linesById: Map<number, { quantity?: number; unit_cost?: number }>,
): boolean {
  // Compare money in whole cents so binary-float noise (100.01 - 100 =
  // 0.010000000000005…) can't trip the strictly-more-than-1¢ rule.
  const centsApart = (a: number, b: number) => Math.round(Math.abs(a - b) * 100)
  for (const m of materials) {
    if (m.estimate_line_item_id == null) continue
    const line = linesById.get(Number(m.estimate_line_item_id))
    if (!line) continue
    if (line.quantity != null && m.quantity != null && m.quantity !== line.quantity) return true
    if (line.unit_cost != null && m.unit_cost != null && centsApart(m.unit_cost, line.unit_cost) > 1) return true
  }
  return false
}

/** Response of GET /tasks/{id}/materials/cost (UOW-3 #3593) — the shared
 *  budget-inclusion-rule figures. All money values are server-computed
 *  Postgres aggregates; the client only formats them (NUMERIC-safe rule —
 *  never do money arithmetic client-side). */
export interface TaskCostTotals {
  task_id?: string
  schedule_type?: string
  /** Materials subtotal (task, or summed over a phase's child tasks). */
  estimated_total?: number
  /** Builder subtotal of the linked estimate line(s); null = no link —
   *  rendered as an em-dash, never $0. */
  estimate_line_subtotal?: number | null
  /** Tasks omitted from a phase baseline because they have no estimate link. */
  unlinked_estimate_task_count?: number
  committed_total?: number
  actual_total?: number
  uncommitted_total?: number
}

/** True when the task/phase has any committed/actual signal worth a row —
 *  the "render only when the task has any cost signal" rule. Exported for
 *  tests. */
export function hasCommittedActualSignal(totals?: TaskCostTotals | null): boolean {
  if (!totals) return false
  return (totals.committed_total ?? 0) > 0 || (totals.actual_total ?? 0) > 0
}

function CostSummaryRow({ label, value, strong = false }: {
  label: string
  value: React.ReactNode
  strong?: boolean
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
      <span style={{
        fontFamily: 'var(--font-sans)', fontSize: 11.5,
        color: 'var(--text-muted)', flexShrink: 0,
      }}>{label}</span>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 12,
        fontWeight: strong ? 700 : 500,
        color: strong ? 'var(--text-primary)' : 'var(--text-secondary)',
        textAlign: 'right', minWidth: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{value}</span>
    </div>
  )
}

// UOW-4F's CombinedEstimateLinesSection (a combined task's removable
// linked-lines list) lived here until UOW-4G (#3672) superseded it with the
// shared related-work card strip (./related-work.tsx), which carries the
// per-line remove, the baseline sum and the split hint forward — one card
// presentation across every peek instead of a competing local one.

function EstimateLineItemSection({
  linkedItem, options, onChange, onCreateNew, canEdit, variance,
  costTotals, isPhase = false,
}: {
  linkedItem?: EstimateLineItemOption | null
  options: EstimateLineItemOption[]
  onChange?: (v: EstimateLineItemOption | null) => void
  onCreateNew?: (description: string) => void
  canEdit: boolean
  /** Cost facet (UOW-1A): the live line's values differ from what the
   *  task's linked material rows carry — render the variance indicator. */
  variance?: boolean
  /** Cost facet (UOW-3): committed/actual figures from the shared budget
   *  inclusion rules. null = not fetched / fetch failed → rows hidden. */
  costTotals?: TaskCostTotals | null
  /** Phase peek: render the estimated/committed/actual rollup summed over
   *  the phase's child tasks instead of task-level rows. */
  isPhase?: boolean
}) {
  // Read-only cost summary of the linked live estimate line — rendered
  // only when the option row carries cost fields (fetched with estimate
  // view permission; the whole section is permission-gated by the caller).
  const hasCostData = !!linkedItem && linkedItem.quantity != null && linkedItem.unit_cost != null
  const markupText = linkedItem ? formatEstimateMarkup(linkedItem) : null
  // UOW-3: committed/actual rows render only when the task has a cost
  // signal (a linked line, or non-zero figures); a failed fetch leaves
  // costTotals null and the rows degrade to hidden.
  const showCommittedActual = !isPhase && !!costTotals &&
    (hasCommittedActualSignal(costTotals) || (hasCostData && !!linkedItem))
  // Phase rollup: estimate lines / materials / committed / actual summed
  // over the phase's child tasks. Rendered only when at least one figure is
  // a real non-zero number — a phase whose children carry no cost signal at
  // all gets no panel (an all-em-dash/all-$0.00 block is noise, not data).
  const showPhaseRollup = isPhase && !!costTotals && (
    hasCommittedActualSignal(costTotals) ||
    (costTotals.estimate_line_subtotal ?? 0) > 0 ||
    (costTotals.estimated_total ?? 0) > 0 ||
    (costTotals.unlinked_estimate_task_count ?? 0) > 0
  )
  return (
    <section style={sectStyle}>
      <SectionLabel
        action={
          linkedItem && canEdit ? (
            <button type="button"
              onClick={() => onChange?.(null)}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontFamily: 'var(--font-sans)', fontSize: 11.5, fontWeight: 600,
                color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
              <Icon name="x" size={10} sw={2} />
              Unlink
            </button>
          ) : null
        }>
        Estimate line item
      </SectionLabel>
      <LinkedEstimateLineItemPicker value={linkedItem}
        onChange={onChange}
        onCreateNew={onCreateNew}
        canEdit={canEdit} options={options} />
      {((hasCostData && linkedItem) || showCommittedActual) && (
        <div style={{
          marginTop: 8, padding: '8px 10px',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-sm)',
          display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          {hasCostData && linkedItem && (
            <>
              <CostSummaryRow label="Qty × unit cost"
                value={`${linkedItem.quantity}${linkedItem.unit ? ` ${linkedItem.unit}` : ''} × ${fmt$(linkedItem.unit_cost)}`} />
              {markupText && <CostSummaryRow label="Markup" value={markupText} />}
              {linkedItem.total != null && (
                <CostSummaryRow label="Line total" value={fmt$(linkedItem.total)} strong />
              )}
              {linkedItem.estimateName && (
                <CostSummaryRow label="Estimate" value={linkedItem.estimateName} />
              )}
            </>
          )}
          {showCommittedActual && costTotals && (
            <>
              <CostSummaryRow label="Committed" value={fmt$(costTotals.committed_total ?? 0)} />
              <CostSummaryRow label="Actual" value={fmt$(costTotals.actual_total ?? 0)} />
            </>
          )}
        </div>
      )}
      {showPhaseRollup && costTotals && (
        <div style={{
          marginTop: 8, padding: '8px 10px',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-sm)',
          display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          <span style={{
            fontFamily: 'var(--font-sans)', fontSize: 10.5, fontWeight: 600,
            color: 'var(--text-muted)', letterSpacing: 0.3, textTransform: 'uppercase',
          }}>Rolled up from tasks in this phase</span>
          {(costTotals.unlinked_estimate_task_count ?? 0) > 0 && (
            <span style={{
              fontFamily: 'var(--font-sans)', fontSize: 11.5, fontWeight: 600,
              color: 'var(--st-warning)',
            }}>
              Partial baseline · {costTotals.unlinked_estimate_task_count}{' '}
              {costTotals.unlinked_estimate_task_count === 1 ? 'task is' : 'tasks are'} unlinked
            </span>
          )}
          {/* Two different estimates, labelled as what they are: the linked
              estimate lines' value, and the materials planned on the tasks.
              A single "Estimated" row hid whichever one the reader wasn't
              thinking of. Em-dash (never $0) when there is no link. */}
          <CostSummaryRow label="Estimate lines" value={fmt$(costTotals.estimate_line_subtotal)} />
          <CostSummaryRow label="Materials" value={fmt$(costTotals.estimated_total ?? 0)} />
          <CostSummaryRow label="Committed" value={fmt$(costTotals.committed_total ?? 0)} />
          <CostSummaryRow label="Actual" value={fmt$(costTotals.actual_total ?? 0)} strong />
        </div>
      )}
      {variance && (
        <button type="button"
          onClick={() => { window.location.href = '/change-orders' }}
          title="Values on this task's materials differ from the live estimate line. Reprice through a change order — nothing is applied automatically."
          style={{
            marginTop: 8, width: '100%',
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 10px',
            background: 'color-mix(in srgb, var(--st-warning) 10%, transparent)',
            border: '1px solid color-mix(in srgb, var(--st-warning) 35%, transparent)',
            borderRadius: 'var(--r-sm)', cursor: 'pointer',
            fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 600,
            color: 'var(--st-warning)', textAlign: 'left',
          }}>
          <Icon name="flag" size={12} sw={2} style={{ flexShrink: 0 }} />
          Price differs from estimate
          <span style={{ flex: 1 }} />
          <Icon name="chevR" size={11} sw={2} style={{ flexShrink: 0 }} />
        </button>
      )}
    </section>
  )
}

function OptionsSection({
  options, onOptionsChange, canEdit, hasPricing = true, mode = 'single',
  clientReview = false, onOptionSelect,
}: {
  options?: DecisionOption[]
  onOptionsChange?: (v: DecisionOption[]) => void
  canEdit: boolean
  hasPricing?: boolean
  mode?: 'single' | 'multiple'
  clientReview?: boolean
  onOptionSelect?: (optionId: string) => void | Promise<void>
}) {
  if (clientReview) {
    const opts = options || []
    return (
      <section style={sectStyle}>
        <SectionLabel>
          <span>Options</span>
          <span style={{
            marginLeft: 8, fontFamily: 'var(--font-sans)', fontSize: 10.5,
            fontWeight: 500, color: 'var(--text-muted)', letterSpacing: 0,
            textTransform: 'none',
          }}>tap to pick</span>
        </SectionLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {opts.map(opt => {
            const isSelected = !!opt.isBaseline
            return (
              <div key={opt.id ?? opt.title}
                style={{
                  border: isSelected
                    ? '1.5px solid var(--brand-gold)'
                    : '1px solid var(--border)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  background: isSelected
                    ? 'color-mix(in srgb, var(--brand-gold) 8%, transparent)'
                    : 'var(--surface)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                }}
              >
                {opt.imageUrl && (
                  <img src={opt.imageUrl} alt=""
                    style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 2 }}>
                    {opt.title}
                  </div>
                  {(opt.brand || opt.sku) && (
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                      {[opt.brand, opt.sku].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {hasPricing && opt.price != null && (
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginTop: 2 }}>
                      ${Number(opt.price).toLocaleString()}
                    </div>
                  )}
                </div>
                {!isSelected && onOptionSelect && opt.id && (
                  <button
                    onClick={() => onOptionSelect(opt.id!)}
                    style={{
                      flexShrink: 0, padding: '5px 12px', fontSize: 12, fontWeight: 600,
                      borderRadius: 6, border: '1px solid var(--border)',
                      background: 'var(--surface)', color: 'var(--text-primary)',
                      cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    Pick this
                  </button>
                )}
                {isSelected && (
                  <span style={{
                    flexShrink: 0, fontSize: 11, fontWeight: 700,
                    color: 'var(--brand-gold)', padding: '4px 8px',
                    background: 'color-mix(in srgb, var(--brand-gold) 12%, transparent)',
                    borderRadius: 5,
                  }}>
                    Selected
                  </span>
                )}
              </div>
            )
          })}
          {opts.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 0' }}>
              No options yet.
            </div>
          )}
        </div>
      </section>
    )
  }

  return (
    <section style={sectStyle}>
      <SectionLabel>
        <span>Options</span>
        <span style={{
          marginLeft: 8, fontFamily: 'var(--font-sans)', fontSize: 10.5,
          fontWeight: 500, color: 'var(--text-muted)', letterSpacing: 0,
          textTransform: 'none',
        }}>{mode === 'multiple' ? 'pick a combination' : 'pick one'}</span>
      </SectionLabel>
      <DecisionOptionsEditor value={options || []} onChange={onOptionsChange}
        canEdit={canEdit} showPricing={hasPricing} mode={mode} />
    </section>
  )
}

function DecisionSettingsSection({
  subKind, selectionMode, closeMode, hasPricing,
  onSubKindChange, onSelectionModeChange, onCloseModeChange, onHasPricingChange,
  canEdit,
}: {
  subKind?: string
  selectionMode?: string
  closeMode?: string
  hasPricing?: boolean
  onSubKindChange?: (v: string) => void
  onSelectionModeChange?: (v: string) => void
  onCloseModeChange?: (v: string) => void
  onHasPricingChange?: (v: boolean) => void
  canEdit: boolean
}) {
  return (
    <section style={sectStyle}>
      <SectionLabel>Decision settings</SectionLabel>
      <SectionGrid cols={2}>
        <PeekField label="Kind" span={12}>
          {/* Always read-only: no backend path (app_tasks decision items, nor
              the decisions-table PATCH handler) persists a post-creation kind
              change — see #2861. Editable segmented control silently no-op'd. */}
          <SubKindPicker value={subKind || 'selection'} onChange={onSubKindChange} canEdit={false} />
        </PeekField>
        {(subKind || 'selection') === 'selection' && (
          <>
            <PeekField label="Selection" span={6}>
              <SelectionModePicker value={selectionMode || 'single'}
                onChange={onSelectionModeChange} canEdit={canEdit} />
            </PeekField>
            <PeekField label="Close" span={6}>
              <CloseModePicker value={closeMode || 'manual'}
                onChange={onCloseModeChange} canEdit={canEdit} />
            </PeekField>
            <PeekField label="Show pricing to client" span={12}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                {canEdit ? (
                  <button type="button"
                    onClick={() => onHasPricingChange?.(!hasPricing)}
                    aria-pressed={hasPricing}
                    style={{
                      width: 36, height: 20, padding: 2,
                      borderRadius: 999,
                      background: hasPricing ? 'var(--brand-gold)' : 'var(--border-strong)',
                      border: 'none', cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center',
                      transition: 'background var(--dur-fast) var(--ease-out)',
                    }}>
                    <span style={{
                      width: 16, height: 16, borderRadius: '50%',
                      background: 'var(--bg-surface)',
                      transform: hasPricing ? 'translateX(16px)' : 'translateX(0)',
                      transition: 'transform var(--dur-fast) var(--ease-out)',
                      display: 'block',
                    }} />
                  </button>
                ) : (
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: hasPricing ? 'var(--brand-gold)' : 'var(--text-muted)',
                    display: 'block',
                  }} />
                )}
                <span style={{
                  fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600,
                  color: 'var(--text-primary)',
                }}>{hasPricing ? 'Prices visible' : 'Hidden from client'}</span>
              </div>
            </PeekField>
          </>
        )}
      </SectionGrid>
    </section>
  )
}

function ChildTasksSection({
  childTasks, onOpenChildTask,
}: {
  childTasks: { id: string; name: string; status?: string; assignee_name?: string | null; due_date?: string | null }[]
  onOpenChildTask?: (taskId: string) => void
}) {
  if (!childTasks || childTasks.length === 0) return null
  const statusDot = (status?: string) =>
    status ? `var(--status-${status.replace(/_/g, '-')}-dot)` : 'var(--text-muted)'
  const done = childTasks.filter(c =>
    c.status === 'completed' || c.status === 'Completed' || c.status === 'cancelled' || c.status === 'Cancelled').length
  return (
    <section style={sectStyle}>
      <SectionLabel>Subtasks · {done}/{childTasks.length} done</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {childTasks.map(c => (
          <button key={c.id} type="button"
            onClick={() => onOpenChildTask?.(c.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', textAlign: 'left',
              padding: '9px 12px', borderRadius: 'var(--r-sm)',
              border: '1px solid var(--border)', background: 'var(--bg-elevated)',
              cursor: onOpenChildTask ? 'pointer' : 'default',
              fontFamily: 'var(--font-sans)',
            }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: statusDot(c.status), flexShrink: 0,
            }} />
            <span style={{
              flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500,
              color: 'var(--text-primary)', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{c.name}</span>
            {c.assignee_name && (
              <span style={{ fontSize: 11.5, color: 'var(--text-muted)', flexShrink: 0 }}>{c.assignee_name}</span>
            )}
            {c.due_date && (
              <span style={{ fontSize: 11.5, color: 'var(--text-muted)', flexShrink: 0 }}>· {c.due_date}</span>
            )}
          </button>
        ))}
      </div>
    </section>
  )
}

// ─── FreeformMaterialRow ───────────────────────────────────────────────

interface FreeformMaterial {
  id: number
  name: string
  vendor?: string
  quantity: number
  unit?: string
  unit_cost?: number
  notes?: string
  stock_status?: string
  // Sourcing facet fields (UOW-1A #3586) — surfaced by
  // GET /api/v1/tasks/{id}/materials (document_line_items compat layer).
  procurement_status?: string | null
  is_tracked?: boolean
  tracking_number?: string
  line_total?: number
  po_id?: number | null
  po_status?: string | null
  linked_bid_request_id?: number | null
  bid_request_status?: string | null
  linked_subcontract_id?: number | null
  subcontract_status?: string | null
  // Estimate provenance (variance indicator). markup_pct here is
  // document_line_items PERCENT scale (0–100), not the estimate fraction.
  estimate_line_item_id?: number | null
  markup_pct?: number | null
  // UOW-5A (§3.12): requirement type (tracker vocabulary — 'subcontract'
  // renders the SUB tag) and, when set, the estimate line this row was
  // auto-drafted from and follows while untouched.
  item_type?: string
  source_estimate_line_id?: number | null
  // UOW-5B (§3.13): the sourcing clock. need_by is the parent task's start
  // date unless need_by_manual; order_by is need_by minus lead time; at_risk
  // / at_risk_reason are the SERVER's verdict, derived per read from one
  // definition and never stored. Schedule facts, not money — they render for
  // everyone who can see the Sourcing facet (§3.1).
  need_by?: string | null
  need_by_manual?: boolean
  order_by?: string | null
  at_risk?: boolean
  at_risk_reason?: string | null
}

// The §3.13 at-risk reason vocabulary lives in lib/procurement-status, beside
// the pipeline stage labels it sits next to on every pill row — one wording
// across the tracker, this facet and the §3.11 cards.

/** SourcingRisk — the server's task-level at-risk rollup, as delivered on
 *  `sourcing_risk` by GET /tasks/{id}/materials. Mirrors api.TaskSourcingRisk. */
export type SourcingRisk = {
  at_risk: boolean
  count: number
  reason?: string | null
  reason_label?: string | null
  by_reason?: Record<string, number> | null
  earliest_need_by?: string | null
}

/** SourcingRiskRollup — §3.13's task-level at-risk line, above the
 *  per-requirement rows.
 *
 *  Rendered from the SERVER's rollup (`sourcing_risk`), not re-derived here.
 *  It used to rank the rows in the browser against a hardcoded severity array
 *  mirroring atrisk.Reasons, which meant the precedence order — the thing that
 *  decides WHICH reason a multi-reason task announces — lived in two places
 *  with nothing pinning them together. internal/api's TestAtRiskConsumersAgree
 *  now asserts this rollup's reason against the per-row verdicts, so the
 *  ranking has one definition and a test that fails when it drifts.
 *
 *  The header cannot claim something the list does not show, because both come
 *  from the same response and every mutation refetches both together (see
 *  loadMaterials). Renders nothing when nothing is at risk: absence is the
 *  empty state, never an empty pill (§5). */
function SourcingRiskRollup({ risk }: { risk?: SourcingRisk | null }) {
  if (!risk || !risk.at_risk || risk.count === 0) return null
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      padding: '6px 10px', marginBottom: 2,
      background: 'color-mix(in srgb, var(--status-blocked-bg) 45%, transparent)',
      border: '1px solid var(--status-blocked-bg)',
      borderRadius: 'var(--r-sm)',
    }}>
      <AtRiskPill reason={risk.reason} count={risk.count} />
      <span style={{
        fontFamily: 'var(--font-sans)', fontSize: 11.5, color: 'var(--text-secondary)',
      }}>
        {risk.count === 1
          ? '1 requirement is late against this task'
          : `${risk.count} requirements are late against this task`}
        {risk.earliest_need_by ? ` · earliest need-by ${risk.earliest_need_by}` : ''}
      </span>
    </div>
  )
}

/** The at-risk pill. One component, used per requirement and (with count) for
 *  the task-level rollup, so the two can never drift apart visually. */
export function AtRiskPill({ reason, count, title }: { reason?: string | null; count?: number; title?: string }) {
  const label = atRiskReasonLabel(reason)
  return (
    <span
      title={title ?? label}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontFamily: 'var(--font-sans)', fontSize: 10.5, fontWeight: 700,
        padding: '1px 7px', borderRadius: 9999,
        background: 'var(--status-blocked-bg)', color: 'var(--status-blocked-tx)',
        whiteSpace: 'nowrap',
      }}>
      {count != null && count > 1 ? `${count} at risk · ${label}` : label}
    </span>
  )
}

const fmt$ = (n?: number | null) => n == null
  ? '—'
  : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })

export type LinkedDocKind = 'po' | 'bid' | 'subcontract'

/** Stock badge — inventory dot + label, same vocabulary/config as
 *  TaskMaterialsSection's StatusDot treatment. Rendered only for materials
 *  that are inventory-tracked ("stock badge where tracked", UOW-1A). */
function StockBadge({ status }: { status: string }) {
  const cfg = STOCK_STATUS_CONFIG[status as StockStatus]
  if (!cfg) return null
  return (
    <span title={`Inventory: ${cfg.label}`} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontFamily: 'var(--font-sans)', fontSize: 10.5, fontWeight: 600,
      color: cfg.color, whiteSpace: 'nowrap',
    }}>
      <span style={{
        width: 7, height: 7, borderRadius: '50%',
        background: cfg.color, flexShrink: 0,
      }}/>
      {cfg.label}
    </span>
  )
}

/** Linked-doc chip — `PO-12 · Sent` vocabulary from the tracker's
 *  linked-artifacts section. Click opens the doc peek stacked. */
function LinkedDocChip({ label, statusLabel, onClick }: {
  label: string
  statusLabel?: string | null
  onClick?: () => void
}) {
  return (
    <button type="button" onClick={onClick}
      title={`Open ${label}${statusLabel ? ` · ${statusLabel}` : ''}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 999,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-strong)',
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 700,
        letterSpacing: 0.3, color: 'var(--text-secondary)',
        whiteSpace: 'nowrap',
      }}>
      {label}
      {statusLabel && (
        <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}>· {statusLabel}</span>
      )}
      <Icon name="chevR" size={9} sw={2.2} style={{ color: 'var(--text-dim)', flexShrink: 0 }}/>
    </button>
  )
}

function FreeformMaterialRow({
  mat, onEdit, onDelete, canEdit, onOpenLinkedDoc, onOpenTracker,
}: {
  mat: FreeformMaterial
  onEdit?: (m: FreeformMaterial) => void
  onDelete?: (id: number) => void
  canEdit: boolean
  onOpenLinkedDoc?: (kind: LinkedDocKind, id: number) => void
  onOpenTracker?: (id: number) => void
}) {
  // Sourcing facet (UOW-1A): stage pill + stock badge + linked-doc chips.
  // Stage gate checks the LABEL map because document_line_items defaults
  // procurement_status to 'not_ordered' (outside the tracker vocabulary) —
  // a bare material must not render an empty pills row.
  const hasStage = !!(mat.procurement_status && PROCUREMENT_STATUS_LABEL[mat.procurement_status])
  const hasStock = !!(mat.is_tracked && mat.stock_status)
  const hasLinks = mat.po_id != null || mat.linked_bid_request_id != null || mat.linked_subcontract_id != null
  // UOW-5A (§3.12): a requirement is one click from its tracker actions —
  // the Tracker chip deep-links to the row's item on the Procurement tracker,
  // where the bid it / order it / subcontract it convert paths live (§3.3).
  // UOW-5B (§3.13): an at-risk requirement always shows its row, even when it
  // has no stage, no stock and no links — being late is exactly the case
  // where the row must not stay silent.
  const hasSourcingRow = hasStage || hasStock || hasLinks || onOpenTracker != null || !!mat.at_risk
  // Non-material requirement tag, same MAT/SVC/SUB vocabulary the tracker
  // list prints (subcontractor requirements never enter material_status).
  const typeTag = mat.item_type === 'subcontract' ? 'SUB'
    : mat.item_type === 'service' || mat.item_type === 'labour' ? 'SVC'
    : null

  return (
    <div style={{
      padding: '8px 10px',
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--r-sm)',
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '6px 1fr auto auto',
        gap: 10, alignItems: 'center',
      }}>
        <span aria-hidden="true" style={{
          width: 4, height: 28, borderRadius: 2,
          background: 'var(--text-muted)', alignSelf: 'center',
        }}/>
        <div style={{ minWidth: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, minWidth: 0,
            fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
            color: 'var(--text-primary)',
          }}>
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{mat.name}</span>
            {typeTag && (
              <span title={typeTag === 'SUB' ? 'Subcontractor requirement' : 'Service requirement'} style={{
                flexShrink: 0, fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700,
                letterSpacing: 0.5, padding: '1px 5px', borderRadius: 4,
                background: 'var(--bg-surface)', border: '1px solid var(--border-strong)',
                color: 'var(--text-muted)',
              }}>{typeTag}</span>
            )}
          </div>
          <div style={{
            fontFamily: 'var(--font-sans)', fontSize: 11.5,
            color: 'var(--text-muted)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {mat.vendor || '—'}{mat.unit ? ` · ${mat.quantity} ${mat.unit}` : ` · qty ${mat.quantity}`}
          </div>
        </div>
        {/* Money renders only when the server sent it — unit_cost is
            field-gated on estimates.view (UOW-1A locked decision; the
            §3.12 drafting copies estimate costs onto requirement rows, so
            an ungated figure here would leak the estimate's cost basis to
            crew tokens). */}
        {mat.unit_cost != null && (
          <div style={{
            textAlign: 'right',
            fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700,
            color: 'var(--text-primary)',
          }}>{fmt$(mat.unit_cost)}</div>
        )}
        {canEdit && (
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" onClick={() => onEdit?.(mat)}
              title="Edit material"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 24, height: 24, borderRadius: 4,
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)',
              }}>
              <Icon name="pencil" size={12} sw={1.8} />
            </button>
            <button type="button" onClick={() => onDelete?.(mat.id)}
              title="Remove material"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 24, height: 24, borderRadius: 4,
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)',
              }}>
              <Icon name="trash" size={12} sw={1.8} />
            </button>
          </div>
        )}
      </div>
      {hasSourcingRow && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
          marginTop: 7, paddingLeft: 16,
        }}>
          {mat.at_risk && (
            <AtRiskPill
              reason={mat.at_risk_reason}
              title={mat.need_by
                ? `${atRiskReasonLabel(mat.at_risk_reason)} — needed by ${mat.need_by}${mat.order_by ? `, order by ${mat.order_by}` : ''}`
                : undefined}
            />
          )}
          {hasStage && <ProcurementStagePill status={mat.procurement_status as string} />}
          {hasStock && <StockBadge status={mat.stock_status as string} />}
          {mat.po_id != null && (
            <LinkedDocChip
              label={`PO-${mat.po_id}`}
              statusLabel={mat.po_status ? poLabel(mat.po_status) : null}
              onClick={onOpenLinkedDoc ? () => onOpenLinkedDoc('po', mat.po_id as number) : undefined}
            />
          )}
          {mat.linked_bid_request_id != null && (
            <LinkedDocChip
              label={`BR-${mat.linked_bid_request_id}`}
              statusLabel={mat.bid_request_status ? bidLabel(mat.bid_request_status) : null}
              onClick={onOpenLinkedDoc ? () => onOpenLinkedDoc('bid', mat.linked_bid_request_id as number) : undefined}
            />
          )}
          {mat.linked_subcontract_id != null && (
            <LinkedDocChip
              label={`SC-${mat.linked_subcontract_id}`}
              statusLabel={mat.subcontract_status ? subLabel(mat.subcontract_status) : null}
              onClick={onOpenLinkedDoc ? () => onOpenLinkedDoc('subcontract', mat.linked_subcontract_id as number) : undefined}
            />
          )}
          {onOpenTracker != null && (
            <LinkedDocChip
              label="Tracker"
              statusLabel={null}
              onClick={() => onOpenTracker(mat.id)}
            />
          )}
        </div>
      )}
    </div>
  )
}

// ─── AddEditMaterialModal ─────────────────────────────────────────────

function AddEditMaterialModal({
  open, onClose, onSave, initial,
}: {
  open: boolean
  onClose: () => void
  onSave: (data: Omit<FreeformMaterial, 'id' | 'stock_status'>) => void
  initial?: FreeformMaterial | null
}) {
  const [name, setName] = React.useState(initial?.name || '')
  const [vendor, setVendor] = React.useState(initial?.vendor || '')
  const [quantity, setQuantity] = React.useState(String(initial?.quantity ?? 1))
  const [unit, setUnit] = React.useState(initial?.unit || '')
  const [cost, setCost] = React.useState(initial?.unit_cost != null ? String(initial.unit_cost) : '')
  const [notes, setNotes] = React.useState(initial?.notes || '')

  React.useEffect(() => {
    if (open) {
      setName(initial?.name || '')
      setVendor(initial?.vendor || '')
      setQuantity(String(initial?.quantity ?? 1))
      setUnit(initial?.unit || '')
      setCost(initial?.unit_cost != null ? String(initial.unit_cost) : '')
      setNotes(initial?.notes || '')
    }
  }, [open, initial])

  if (!open) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    onSave({
      name: name.trim(),
      vendor: vendor.trim() || undefined,
      quantity: Math.max(0.001, parseFloat(quantity) || 1),
      unit: unit.trim() || undefined,
      unit_cost: cost !== '' ? parseFloat(cost) : undefined,
      notes: notes.trim() || undefined,
    })
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '7px 10px',
    fontFamily: 'var(--font-sans)', fontSize: 13,
    color: 'var(--text-primary)',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--r-sm)',
    boxSizing: 'border-box' as const,
    outline: 'none',
  }
  const labelStyle: React.CSSProperties = {
    fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 700,
    color: 'var(--text-muted)', letterSpacing: 0.5,
    textTransform: 'uppercase' as const,
    display: 'block', marginBottom: 4,
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.45)',
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)',
        padding: 20,
        width: 340,
        maxWidth: 'calc(100vw - 32px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        <div style={{
          fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: 700,
          color: 'var(--text-primary)', marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          {initial ? 'Edit material' : 'Add material'}
          <button type="button" onClick={onClose} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center',
          }}>
            <Icon name="x" size={14} sw={2} />
          </button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>Name *</label>
            <input style={inputStyle} value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Drywall mud 25kg" autoFocus required />
          </div>
          <div>
            <label style={labelStyle}>Vendor</label>
            <input style={inputStyle} value={vendor} onChange={e => setVendor(e.target.value)}
              placeholder="e.g. Home Depot" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Qty</label>
              <input style={inputStyle} type="number" min="0.001" step="any"
                value={quantity} onChange={e => setQuantity(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Unit</label>
              <input style={inputStyle} value={unit} onChange={e => setUnit(e.target.value)}
                placeholder="bag, sheet, ea…" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Unit cost ($)</label>
            <input style={inputStyle} type="number" min="0" step="any"
              value={cost} onChange={e => setCost(e.target.value)}
              placeholder="0.00" />
          </div>
          <div>
            <label style={labelStyle}>Notes</label>
            <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 56 }}
              value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Any notes about this material…" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{
              padding: '7px 14px', borderRadius: 'var(--r-sm)',
              background: 'transparent', border: '1px solid var(--border-strong)',
              fontFamily: 'var(--font-sans)', fontSize: 13, cursor: 'pointer',
              color: 'var(--text-muted)',
            }}>Cancel</button>
            <button type="submit" style={{
              padding: '7px 16px', borderRadius: 'var(--r-sm)',
              background: 'var(--brand-gold)', border: 'none',
              fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
              color: '#000', cursor: 'pointer',
            }}>
              {initial ? 'Save' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── FilesSection ─────────────────────────────────────────────────────

export function isImageAttachment(a: PeekAttachment): boolean {
  return isImageFile(a.filename, a.mime_type)
}

function toViewerAttachment(a: PeekAttachment): ViewerAttachment {
  return { url: attachmentUrl(a), filename: a.filename, mimeType: a.mime_type, size: a.size }
}

// File-type badge — mirrors FileBadge/FILE_KIND in create-task-mobile.jsx
// (the shared mobile vocabulary the web Files zone follows for attachment
// rows: a 2-3 letter kind label instead of a generic paperclip). The design
// brief colors each kind (PDF/DOC/XLS) with a distinct hue, but status hues
// are reserved for StatusPill/status-dot contexts here (see the "RULE" note
// in tokens/status.css) — so this uses one neutral treatment for every kind
// and differentiates purely by label text.
const FILE_KIND: Record<string, string> = { pdf: 'PDF', doc: 'DOC', xls: 'XLS', file: 'FILE' }
function fileKind(a: PeekAttachment): keyof typeof FILE_KIND {
  const mt = a.mime_type || ''
  const name = (a.filename || '').toLowerCase()
  if (mt === 'application/pdf' || name.endsWith('.pdf')) return 'pdf'
  if (mt.includes('word') || /\.docx?$/.test(name)) return 'doc'
  if (mt.includes('sheet') || mt.includes('excel') || mt === 'text/csv' || /\.(xlsx?|csv)$/.test(name)) return 'xls'
  return 'file'
}
function FileBadge({ kind }: { kind: keyof typeof FILE_KIND }) {
  return (
    <span style={{
      width: 28, height: 28, borderRadius: 7, flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-elevated)', color: 'var(--text-muted)',
      fontFamily: 'var(--font-mono)', fontSize: 7.5, fontWeight: 700, letterSpacing: 0.3,
    }}>{FILE_KIND[kind] || FILE_KIND.file}</span>
  )
}

// ─── PhotosSection — image attachments, split from Files per design spec ──
//
// Photos and Files are visually separate drop zones (task-peek-web.jsx),
// not one combined list. Both still upload through the same generic
// /tasks/{id}/attachments endpoint — this section just filters the shared
// attachment list down to images and renders them as a thumbnail grid
// instead of a file-row list.

function PhotosSection({
  attachments, onAttachFile, onRemoveAttachment, canEdit, openPreview,
}: {
  attachments: PeekAttachment[]
  onAttachFile?: () => void
  onRemoveAttachment?: (id: string) => void
  canEdit: boolean
  openPreview: (a: PeekAttachment) => void
}) {
  const photos = attachments.filter(isImageAttachment)
  return (
    <section style={sectStyle}>
      <SectionLabel
        action={canEdit ? (
          <button type="button" onClick={onAttachFile} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontFamily: 'var(--font-sans)', fontSize: 11.5, fontWeight: 600,
            color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <Icon name="paperclip" size={11} sw={2} />
            Add photo
          </button>
        ) : null}>
        Photos · {photos.length}
      </SectionLabel>
      {photos.length > 0 ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {photos.map((p) => (
            <div key={p.id} style={{
              position: 'relative', width: 66, height: 66,
              borderRadius: 'var(--r-sm)', border: '1px solid var(--border)',
              overflow: 'hidden', flexShrink: 0,
            }}>
              <img src={attachmentThumbUrl(p)} alt="" loading="lazy" style={{
                width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer',
              }} onClick={() => openPreview(p)} />
              {canEdit && onRemoveAttachment && (
                <button type="button" onClick={() => onRemoveAttachment(p.id)} aria-label="Remove photo"
                  style={{
                    position: 'absolute', top: 4, right: 4, width: 18, height: 18, minHeight: 0, minWidth: 0,
                    borderRadius: '50%', border: 'none',
                    background: 'color-mix(in srgb, var(--text-primary) 75%, transparent)',
                    color: 'var(--bg-surface)', cursor: 'pointer', display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                  <Icon name="x" size={10} sw={2.6} />
                </button>
              )}
            </div>
          ))}
          {canEdit && (
            <button type="button" onClick={onAttachFile} style={{
              width: 66, height: 66, borderRadius: 'var(--r-sm)', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--brand-gold-soft, var(--bg-elevated))',
              border: '1.5px dashed var(--brand-gold-border, var(--border-strong))',
              color: 'var(--brand-gold)', flexShrink: 0,
            }}>
              <Icon name="plus" size={17} sw={2.4} />
            </button>
          )}
        </div>
      ) : canEdit ? (
        <button type="button" onClick={onAttachFile} style={{
          width: '100%', minHeight: 66, borderRadius: 'var(--r-sm)', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          background: 'var(--brand-gold-soft, var(--bg-elevated))',
          border: '1.5px dashed var(--brand-gold-border, var(--border-strong))',
          color: 'var(--brand-gold)', fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 600,
        }}>
          <Icon name="plus" size={16} sw={2.2} /> Drop or click to add photos
        </button>
      ) : (
        <div style={{
          fontFamily: 'var(--font-sans)', fontSize: 12.5, color: 'var(--text-dim)', fontStyle: 'italic',
        }}>No photos.</div>
      )}
    </section>
  )
}

function FilesSection({
  materials, attachments, autoMaterials = [], freeformMaterials = [], sourcingRisk, onAttachFile, onOpenSpec, canEdit, kind,
  onAddMaterial, onEditMaterial, onDeleteMaterial, onRemoveAttachment, openPreview,
  onOpenLinkedDoc, onOpenTracker, materialStatus,
}: {
  materials?: unknown[]
  attachments?: PeekAttachment[]
  autoMaterials?: { specId: string; optionId?: string }[]
  freeformMaterials?: FreeformMaterial[]
  sourcingRisk?: SourcingRisk | null
  onAttachFile?: () => void
  onOpenSpec?: (id: string) => void
  canEdit: boolean
  kind: string
  onAddMaterial?: () => void
  onEditMaterial?: (m: FreeformMaterial) => void
  onDeleteMaterial?: (id: number) => void
  onRemoveAttachment?: (id: string) => void
  openPreview: (a: PeekAttachment) => void
  /** Sourcing facet (UOW-1A) — opens a material's linked PO/bid/subcontract
   *  in a stacked doc peek. */
  onOpenLinkedDoc?: (kind: LinkedDocKind, id: number) => void
  /** Sourcing facet (UOW-5A §3.12) — one click from a requirement to its
   *  tracker actions (bid it / order it / subcontract it, §3.3). */
  onOpenTracker?: (id: number) => void
  /** Task-level material readiness rollup for the Materials sub-header. */
  materialStatus?: string | null
}) {
  const showMaterials = kind === 'task'
  const hasAutoMaterials = showMaterials && autoMaterials.length > 0
  const hasFreeformMaterials = showMaterials && freeformMaterials.length > 0
  const hasMaterials = showMaterials && (hasAutoMaterials || hasFreeformMaterials)

  const isImageAtt = isImageAttachment
  // Photos render in their own PhotosSection above — this section is non-image files only.
  const nonImageAttachments = (attachments || []).filter((a) => !isImageAtt(a))

  return (
    <section style={sectStyle}>
      <SectionLabel
        action={canEdit ? (
          <button type="button" onClick={onAttachFile} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontFamily: 'var(--font-sans)', fontSize: 11.5, fontWeight: 600,
            color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <Icon name="paperclip" size={11} sw={2} />
            Attach file
          </button>
        ) : null}>
        {showMaterials ? 'Files & materials' : 'Files'}
      </SectionLabel>
      {showMaterials && (
        <div style={{ marginBottom: canEdit ? 12 : 8 }}>
          <div style={{
            fontFamily: 'var(--font-sans)', fontSize: 11.5, fontWeight: 700,
            color: 'var(--text-muted)', letterSpacing: 0.4,
            textTransform: 'uppercase' as const, marginBottom: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Materials
              {materialStatus && (
                <MaterialStatusPill status={materialStatus as MaterialStatus} />
              )}
            </span>
            {canEdit && (
              <button type="button" onClick={onAddMaterial} style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontFamily: 'var(--font-sans)', fontSize: 11.5, fontWeight: 600,
                color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
                <Icon name="plus" size={11} sw={2.2} />
                Add
              </button>
            )}
          </div>
          {hasAutoMaterials && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {autoMaterials.map((m) => (
                <FromSpecMaterialRow key={m.specId} specId={m.specId}
                  onOpenSpec={onOpenSpec} canEdit={canEdit} />
              ))}
              <div style={{
                marginTop: 2, fontFamily: 'var(--font-sans)', fontSize: 11,
                color: 'var(--text-muted)', fontStyle: 'italic',
              }}>
                Auto-populated from approved Decisions on this task. Edit via the spec.
              </div>
            </div>
          )}
          {hasFreeformMaterials && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: hasAutoMaterials ? 8 : 0 }}>
              <SourcingRiskRollup risk={sourcingRisk} />
              {freeformMaterials.map((m) => (
                <FreeformMaterialRow key={m.id} mat={m} canEdit={canEdit}
                  onEdit={onEditMaterial} onDelete={onDeleteMaterial}
                  onOpenLinkedDoc={onOpenLinkedDoc} onOpenTracker={onOpenTracker} />
              ))}
            </div>
          )}
          {!hasMaterials && (
            <div style={{
              fontFamily: 'var(--font-sans)', fontSize: 12.5,
              color: 'var(--text-muted)', fontStyle: 'italic',
            }}>No materials yet — add one; it will appear in Procurement automatically.</div>
          )}
        </div>
      )}
      <div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 8,
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontFamily: 'var(--font-sans)', fontSize: 12.5,
            color: 'var(--text-muted)',
          }}>
            <Icon name="paperclip" size={12} sw={1.7} />
            {nonImageAttachments.length} file{nonImageAttachments.length === 1 ? '' : 's'}
          </span>
        </div>
        {/* Attachment list */}
        {nonImageAttachments.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
            {nonImageAttachments.map((att) => (
              <div key={att.id} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 8px',
                background: 'var(--bg-elevated)',
                borderRadius: 'var(--r-sm)',
                border: '1px solid var(--border)',
              }}>
                {/* Thumbnail for images, type badge otherwise */}
                {isImageAtt(att) ? (
                  <img src={attachmentThumbUrl(att)} alt="" loading="lazy" style={{
                    width: 28, height: 28, borderRadius: 4, objectFit: 'cover',
                    flexShrink: 0, border: '1px solid var(--border)', cursor: 'pointer',
                  }} onClick={() => openPreview(att)} />
                ) : (
                  <FileBadge kind={fileKind(att)} />
                )}
                <button
                  type="button"
                  onClick={() => openPreview(att)}
                  title="Preview"
                  style={{
                    flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none',
                    cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12.5,
                    color: 'var(--text-primary)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                  {att.filename}
                </button>
                {canEdit && onRemoveAttachment && (
                  <button
                    type="button"
                    onClick={() => onRemoveAttachment(att.id)}
                    aria-label={`Remove ${att.filename}`}
                    title="Remove attachment"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                      color: 'var(--text-muted)', display: 'flex', alignItems: 'center', flexShrink: 0,
                    }}
                  >
                    <Icon name="x" size={12} sw={2} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {/* Drop zone */}
        {canEdit && nonImageAttachments.length === 0 && (
          <div style={{
            padding: 18,
            border: '1.5px dashed var(--border-strong)',
            borderRadius: 'var(--r-sm)',
            textAlign: 'center',
            fontFamily: 'var(--font-sans)', fontSize: 12,
            color: 'var(--text-muted)', lineHeight: 1.5,
          }}>
            Drop files here or click "Attach file" above.<br />
            <span style={{ fontSize: 11 }}>pdf (max 60 MB) · docx / xlsx / csv / txt (max 25 MB)</span>
          </div>
        )}
        {!canEdit && nonImageAttachments.length === 0 && (
          <div style={{
            fontFamily: 'var(--font-sans)', fontSize: 12.5,
            color: 'var(--text-dim)', fontStyle: 'italic',
          }}>No files attached.</div>
        )}
      </div>
    </section>
  )
}

// ─── ActivitySection — comments + auto-logged events, composer at the top ──
//
// Backed by the real GET/POST /api/v1/tasks/{id}/comments endpoints
// (app_task_comments table) — not a stubbed local thread. Auto-logged
// system events (status changes, field edits) are server-generated on
// every task update and share this same thread with `kind: 'event'`.
//
// The list + composer are CommentPanel (organisms/CommentPanel.tsx) in its
// 'inline' variant — @mention autocomplete, attachments-on-a-comment, and
// mention notifications all live there so this peek and mobile task detail
// share one implementation instead of two bespoke composers (#1536).

function ActivitySection({
  taskId, canEdit, isCreate, refreshSignal,
}: {
  taskId: string
  canEdit: boolean
  isCreate: boolean
  refreshSignal: number
}) {
  const [count, setCount] = React.useState(0)

  return (
    <section style={sectStyle}>
      <SectionLabel>Activity{isCreate ? '' : ` · ${count}`}</SectionLabel>
      {isCreate ? (
        <div style={{
          fontFamily: 'var(--font-sans)', fontSize: 12.5, color: 'var(--text-dim)',
        }}>Activity starts once the task is created.</div>
      ) : (
        <CommentPanel
          entityType="task"
          entityId={taskId}
          isOpen
          variant="inline"
          canEdit={canEdit}
          allowAttachments
          refreshSignal={refreshSignal}
          onCountChange={setCount}
          onClose={() => {}}
        />
      )}
    </section>
  )
}

// ─── TaskDetailPeek — the integrated component ────────────────────────

// TaskDetailPeek wraps its stateful body (TaskDetailPeekInner) with a per-task
// `key`, forcing a clean remount whenever the task identity changes. The body
// initializes ALL its field state from `task` on mount only, and consumers
// mount the peek always-on (SidePeek toggles visibility via transform and never
// unmounts its children). Without this key the body retained stale state AND
// stacked a fresh Title <textarea> on every open — "a new title field appears
// each time the side-peek opens" (#1440). Keying here fixes it for ALL ~21
// consumers at once (several pages had hand-added `key={id}` to work around this
// per-call-site — now redundant but harmless). Do NOT remove this wrapper.
export function TaskDetailPeek(props: TaskDetailPeekProps) {
  return <TaskDetailPeekInner key={props.task?.id ?? 'none'} {...props} />
}

function TaskDetailPeekInner({
  open, onClose, onExpand,
  task,
  kind: kindProp,
  canEdit = true,
  estimate = false,
  taskTypeEnabled = true,
  onOpenLinkedSpec,
  onOpenTask,
  onBack,
  breadcrumb,
  onKindChange,
  phases = [],
  locations = [],
  fsRoomOptions = [],
  projectOptions = [],
  assigneeOptions = [],
  goalOptions = [],
  equipmentOptions = [],
  taskTypeOptions = [],
  depItems = EMPTY_DEP_ITEMS,
  blockingTasks = [],
  onRemoveDependency,
  specOptions = [],
  parentTaskOptions = [],
  childTasks = [],
  onOpenChildTask,
  onFieldSave,
  onEstimateLinksChanged,
  onFileUpload,
  onFileRemove,
  onDuplicate,
  onDelete,
  canEditField,
  footer,
  mode: viewMode = 'normal',
  onOptionSelect,
  mobile = false,
  zIndex,
}: TaskDetailPeekProps) {
  const t: PeekTask = task
    ? ('schedule_type' in task || 'assignee_names' in task
        ? normalizeAppTaskToPeekTask(task as AppTask)
        : task as PeekTask)
    : {}
  const kindList = estimate ? ESTIMATE_KINDS : SCHEDULE_KINDS

  // Dependency-catalogue fallback. Most surfaces that open this peek (mobile
  // schedule, internal tasks, goals, callbacks, kiosk, procurement, dashboard
  // widgets, …) don't pass `depItems`, so the dependency picker was empty
  // everywhere except the main Schedule. When no catalogue is supplied, load
  // the task's project schedule items so deps are always pickable.
  const [fetchedDepItems, setFetchedDepItems] = React.useState<DepItem[]>([])
  React.useEffect(() => {
    if (!open) return
    if (depItems && depItems.length > 0) { setFetchedDepItems([]); return }
    const pid = t.project
      ?? (t as { project_id?: string | number; projectId?: string | number }).project_id
      ?? (t as { projectId?: string | number }).projectId
    if (!pid) return
    let cancelled = false
    Promise.all([
      authedFetch(`/api/v1/tasks?project_id=${pid}&limit=500`, {}, { redirectOnAuthFailure: false }).then(r => (r.ok ? r.json() : null)).catch(() => null),
      // BUG-DEP-PICKER-DISAMBIG — phase names, so same-named tasks in
      // different phases are distinguishable here too, not just on the
      // main Schedule page.
      appPhases.list({ project_id: pid }).catch(() => []),
    ]).then(([res, phases]: [{ data?: unknown[]; items?: unknown[] } | unknown[] | null, AppTask[]]) => {
      if (cancelled || !res) return
      const arr = (Array.isArray(res) ? res : (res.data ?? res.items ?? [])) as Array<
        { id: string; title?: string; name?: string; phase_id?: string | null; location_name?: string | null }
      >
      const phaseNameById = new Map((phases || []).map(p => [String(p.id), p.name || (p as { title?: string }).title || '']))
      const selfId = (t as { id?: string }).id
      const out: DepItem[] = []
      for (const tk of arr) {
        if (!tk?.id || tk.id === selfId) continue
        out.push({
          id: String(tk.id), name: tk.title || tk.name || 'Untitled', type: 'task',
          phase: tk.phase_id ? (phaseNameById.get(String(tk.phase_id)) || undefined) : undefined,
          room: tk.location_name || undefined,
        })
      }
      // The /api/v1/tasks fetch above defaults to schedule_type='task' only —
      // phases must be pickable dependency targets too (#2801), so append
      // them from the already-fetched phase list.
      for (const ph of phases || []) {
        const phId = String(ph.id)
        if (!phId || phId === selfId) continue
        out.push({ id: phId, name: phaseNameById.get(phId) || 'Untitled phase', type: 'phase' })
      }
      setFetchedDepItems(out)
    }).catch(() => { /* non-fatal — picker just stays empty */ })
    return () => { cancelled = true }
  }, [open, depItems, t.project, (t as { project_id?: string | number }).project_id, (t as { projectId?: string | number }).projectId, (t as { id?: string }).id])

  const effectiveDepItems = (depItems && depItems.length > 0) ? depItems : fetchedDepItems

  // fieldEditable resolves per-field edit permission.
  // Mode A (canEdit=true, canEditField present): gate is a disabler — return false to lock a field.
  // Mode B (canEdit=false, canEditField present): gate is an allowlist — return true to unlock a field.
  // When canEditField is absent: canEdit governs uniformly.
  const fieldEditable = React.useCallback((key: string) => {
    if (canEditField) return canEditField(key)
    return canEdit ?? true
  }, [canEdit, canEditField])

  // Cost visibility follows estimate permissions (UOW-1A locked decision):
  // the Estimate line item section — picker AND read-only cost summary — is
  // entirely ABSENT for roles that can't view estimates (never disabled).
  // Same `can('estimates','view')` gate the sidebar uses for estimate routes
  // (EST-06, #733/#786). Crew keep the full Sourcing facet.
  const { can } = usePermissions()
  const canViewEstimates = can('estimates', 'view')

  // Internally source task types so callers don't need to pass taskTypeOptions.
  const internalTypeLabels = useRefList('task_types')
  const effectiveTypeOptions: TaskTypeOption[] = React.useMemo(() => {
    if (taskTypeOptions.length > 0) return taskTypeOptions
    return internalTypeLabels.map(label => ({ id: label, label, dot: 'var(--text-muted)' }))
  }, [taskTypeOptions, internalTypeLabels])

  const defaultKind: TaskPeekKind = kindProp || (t.kind as TaskPeekKind) || 'task'
  const [kind, setKind] = React.useState<string>(defaultKind)
  React.useEffect(() => {
    if (kindProp) setKind(kindProp)
  }, [kindProp])

  const handleKindChange = (k: string) => {
    setKind(k)
    onKindChange?.(k as TaskPeekKind)
    // Persist through the same auto-save path every other field uses. Most
    // callers (ProjectSchedulePage, TasksPage, ScheduleListView, ...) only
    // wire onFieldSave, not onKindChange — without this, the chip flips
    // locally but the change never reaches the backend, so the next
    // fetchSchedule()/refetch silently reverts it to whatever schedule_type
    // is still in the DB (looks saved, isn't — see #2799).
    onFieldSave?.('schedule_type', k)
  }

  // Per-field local state (blur-to-save semantics)
  const [name, setName] = React.useState(t.name || '')
  const [priority, setPriority] = React.useState(t.priority || 'normal')
  const [status, setStatus] = React.useState(() => {
    const s = t.status
    if (s) return s
    if (defaultKind === 'decision') return 'draft'
    if (defaultKind === 'milestone') return 'pending'
    if (defaultKind === 'phase') return 'in_progress'
    return 'not_started'
  })
  const [assignees, setAssignees] = React.useState<string[]>(t.assignees || [])
  const [decider, setDecider] = React.useState<string | null>(t.decider || null)
  const [project, setProject] = React.useState<string | null>(t.project || null)
  const [phase, setPhase] = React.useState<string | null>(t.phase || null)
  const [room, setRoom] = React.useState<string | null>(t.room || null)
  const [fsRoom, setFsRoom] = React.useState<string | null>(t.fsRoom || null)
  const [fsRoomName, setFsRoomName] = React.useState<string | null>(t.fsRoomName || null)
  const [start, setStart] = React.useState<string | null>(t.start || null)
  const [due, setDue] = React.useState<string | null>(t.due || null)
  const [duration, setDuration] = React.useState(t.duration || '')
  const [dueBy, setDueBy] = React.useState<string | null>(t.dueBy || null)
  const [orderLead, setOrderLead] = React.useState<string | null>(t.orderLead || null)
  const [milestoneDate, setMilestoneDate] = React.useState<string | null>(t.milestoneDate || null)
  const [notes, setNotes] = React.useState(t.notes || '')
  const [deps, setDeps] = React.useState<Dependency[]>(t.dependencies || [])
  const [linkedSpec, setLinkedSpec] = React.useState<SpecOption | null>(t.linkedSpec || null)
  const [options, setOptions] = React.useState<DecisionOption[]>(t.options || [])
  const [parentTask, setParentTask] = React.useState<string | null>(t.parentTask || null)
  const [goal, setGoal] = React.useState(t.goal || 'none')
  const [repeat, setRepeat] = React.useState(t.repeat || 'none')
  const [billable, setBillable] = React.useState(t.billable || 'billable')
  const [equipment, setEquipment] = React.useState<string[]>(t.equipment || [])
  const [taskType, setTaskType] = React.useState(t.taskType || 'pre')
  const [subKind, setSubKind] = React.useState(t.subKind || 'selection')
  const [selectionMode, setSelectionMode] = React.useState(t.selectionMode || 'single')
  const [closeMode, setCloseMode] = React.useState(t.closeMode || 'manual')
  const [hasPricing, setHasPricing] = React.useState(
    t.hasPricing == null ? true : t.hasPricing
  )
  const [attachments, setAttachments] = React.useState<PeekAttachment[]>(t.attachments || [])
  const [flag, setFlag] = React.useState<TaskFlag | null>(t.flag ?? null)
  const [flagOpen, setFlagOpen] = React.useState(false)
  const [flagBusy, setFlagBusy] = React.useState(false)
  const [resolveBusy, setResolveBusy] = React.useState(false)
  // Schedule↔Estimate link (#2936) — id is the source of truth (from t);
  // the full row for display is resolved from estimateLineItemOptions below,
  // since the peek doesn't get a hydrated join from the backend.
  const [estimateLineItemId, setEstimateLineItemId] = React.useState<string | null>(t.estimateLineItemId || null)
  const [estimateLineItemOptions, setEstimateLineItemOptions] = React.useState<EstimateLineItemOption[]>([])
  // The estimate a newly-created line item gets attached to via "Create ..."
  // in the picker — the task's project's first (usually only) estimate.
  const [primaryEstimateId, setPrimaryEstimateId] = React.useState<string | null>(null)

  // Sync all t-derived local state when the peek receives a different task.
  // SidePeek's open prop only toggles transform (translateX); the component
  // does NOT unmount on close. Without this effect, the useState initializers
  // above run only on first mount, so every subsequent task loaded into the
  // same peek instance shows stale data from the FIRST task — symptoms include
  // every task showing the same project after switching, subtasks inheriting
  // wrong project, edits applying to wrong record.
  //
  // Keyed on t.id only: re-running for unrelated parent re-renders would
  // clobber in-flight user edits (e.g. typing in notes).
  React.useEffect(() => {
    setName(t.name || '')
    setPriority(t.priority || 'normal')
    setStatus(() => {
      const s = t.status
      if (s) return s
      if (defaultKind === 'decision') return 'draft'
      if (defaultKind === 'milestone') return 'pending'
      if (defaultKind === 'phase') return 'in_progress'
      return 'not_started'
    })
    setAssignees(t.assignees || [])
    setDecider(t.decider || null)
    setProject(t.project || null)
    setPhase(t.phase || null)
    setRoom(t.room || null)
    setFsRoom(t.fsRoom || null)
    setFsRoomName(t.fsRoomName || null)
    setStart(t.start || null)
    setDue(t.due || null)
    setDuration(t.duration || '')
    setDueBy(t.dueBy || null)
    setOrderLead(t.orderLead || null)
    setMilestoneDate(t.milestoneDate || null)
    setNotes(t.notes || '')
    setDeps(t.dependencies || [])
    setLinkedSpec(t.linkedSpec || null)
    setEstimateLineItemId(t.estimateLineItemId || null)
    setOptions(t.options || [])
    setParentTask(t.parentTask || null)
    setGoal(t.goal || 'none')
    setRepeat(t.repeat || 'none')
    setBillable(t.billable || 'billable')
    setEquipment(t.equipment || [])
    setTaskType(t.taskType || 'pre')
    setSubKind(t.subKind || 'selection')
    setSelectionMode(t.selectionMode || 'single')
    setCloseMode(t.closeMode || 'manual')
    setHasPricing(t.hasPricing == null ? true : t.hasPricing)
    setAttachments(t.attachments || [])
    setFlag(t.flag ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t.id])

  // `dependencies` is populated by the HOST PAGE via an async fetch that
  // starts only after the task is selected (see useTaskPeekDependencies),
  // so it routinely arrives on a LATER render than t.id — the [t.id] sync
  // effect above already ran with the stale/empty value by then and never
  // re-fires. `blockingTasks` doesn't have this bug because it's read
  // straight from the prop in DependenciesSection with no local copy; `deps`
  // needs its own resync so the Dependencies list doesn't stay stuck empty
  // after reopening a task whose predecessors loaded after the initial sync.
  React.useEffect(() => {
    setDeps(t.dependencies || [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t.dependencies])

  // Schedule↔Estimate link (#2936) — self-fetch the task's project's
  // estimate line items to populate the picker. Self-contained (no new prop
  // threaded through every page that renders this peek, mirroring how
  // fetchedDepItems above self-fetches deps) since only 'task'/'phase' kinds
  // can link, and not every host page has a project-scoped estimate to hand
  // in anyway.
  React.useEffect(() => {
    if (!open) return
    if (kind !== 'task' && kind !== 'phase') return
    // Permission-less users never see the section (UOW-1A) — skip the
    // fetches entirely rather than firing requests EST-06 will 403.
    if (!canViewEstimates) { setEstimateLineItemOptions([]); setPrimaryEstimateId(null); return }
    if (!project) { setEstimateLineItemOptions([]); setPrimaryEstimateId(null); return }
    let cancelled = false
    estimates.list({ project_id: project }).then(async (page) => {
      if (cancelled) return
      const ests = page.data || []
      if (ests.length === 0) { setEstimateLineItemOptions([]); setPrimaryEstimateId(null); return }
      setPrimaryEstimateId(ests[0].id)
      const lists = await Promise.all(ests.map(e => estimateLineItems.list(e.id).catch(() => [])))
      if (cancelled) return
      const opts: EstimateLineItemOption[] = []
      lists.forEach((items, idx) => {
        const estName = ests[idx].title || `Estimate #${ests[idx].id}`
        const estTitle = ests.length > 1 ? estName : undefined
        for (const li of items) {
          if (li.is_section_header) continue
          opts.push({
            id: Number(li.id), description: li.description || '', total: li.total, estimateTitle: estTitle,
            // Cost facet (UOW-1A): read-only summary + variance inputs.
            // markup_pct stays in the estimate's FRACTION scale here.
            quantity: li.quantity, unit: li.unit, unit_cost: li.unit_cost,
            markup_pct: li.markup_pct, markup_type: li.markup_type,
            markup_flat_amount: li.markup_flat_amount, markup_amount: li.markup_amount,
            subtotal: li.subtotal,
            estimateName: estName,
          })
        }
      })
      setEstimateLineItemOptions(opts)
    }).catch(() => { if (!cancelled) { setEstimateLineItemOptions([]); setPrimaryEstimateId(null) } })
    return () => { cancelled = true }
  }, [open, project, kind, canViewEstimates])

  const linkedEstimateLineItem = React.useMemo(
    () => estimateLineItemOptions.find(o => String(o.id) === estimateLineItemId) || null,
    [estimateLineItemOptions, estimateLineItemId],
  )

  const [creatingEstimateLineItem, setCreatingEstimateLineItem] = React.useState(false)
  const handleCreateEstimateLineItem = React.useCallback(async (description: string) => {
    if (!primaryEstimateId || creatingEstimateLineItem) return
    setCreatingEstimateLineItem(true)
    try {
      // Mirrors the estimate editor's add-line-item defaults (EstimateEditorPage
      // addLineItem) — a real estimate row, then linked, per the ticket's ask.
      const created = await estimateLineItems.create(primaryEstimateId, {
        description,
        cost_type: 'material',
        quantity: 1,
        unit: 'ea',
        unit_cost: 0,
        markup_pct: 0,
        tax_rate: 0,
        sort_order: (estimateLineItemOptions.length + 1) * 10,
        is_section_header: false,
      })
      setEstimateLineItemOptions(prev => [...prev, { id: Number(created.id), description: created.description || description, total: created.total }])
      setEstimateLineItemId(String(created.id))
      onFieldSave?.('estimate_line_item_id', created.id)
    } catch {
      // Non-fatal — the picker just stays open with nothing created.
    } finally {
      setCreatingEstimateLineItem(false)
    }
  }, [primaryEstimateId, creatingEstimateLineItem, estimateLineItemOptions.length, onFieldSave])

  // Lazy-bootstrap the active workday calendar from the backend on first use.
  // Idempotent — only runs when no calendar is cached in the module yet.
  // Mirrors TaskForm.tsx's create-form bootstrap so the When calc loop below
  // honors the same company non-working-day calendar.
  React.useEffect(() => {
    if (getActiveWorkdayCalendar()) return
    scheduleCalendars.getDefault()
      .then(cal => setActiveWorkdayCalendar(cal))
      .catch(() => { /* fall back to spec defaults silently */ })
  }, [])

  // ── Workday-aware Start/Due/Duration calc loop (PEEK-WHEN-01) ──────────
  // When any TWO of Start / Due / Duration are set, auto-calculate the
  // third — work-day based (skips weekends + company non-working days via
  // the shared lib/workday calendar). Mirrors TaskForm.tsx's create-form
  // handlers so editing an existing task behaves the same as creating one.
  const handleWhenStartChange = (v: string | null) => {
    setStart(v)
    onFieldSave?.('start', v)
    const s = v ? parseDatetimeLocal(v) : null
    if (!s) return
    const d = due ? parseDatetimeLocal(due) : null
    if (d) {
      const mins = diffWorkingMinutes(s, d)
      const newDuration = mins > 0 ? formatWorkdayDuration(mins) : ''
      setDuration(newDuration)
      onFieldSave?.('duration', newDuration)
      return
    }
    const mins = parseWorkdayDuration(duration)
    if (mins) {
      const newDue = addWorkingMinutes(s, mins).toISOString()
      setDue(newDue)
      onFieldSave?.('due', newDue)
    }
  }

  const handleWhenDueChange = (v: string | null) => {
    setDue(v)
    onFieldSave?.('due', v)
    const d = v ? parseDatetimeLocal(v) : null
    if (!d) return
    const s = start ? parseDatetimeLocal(start) : null
    if (s) {
      const mins = diffWorkingMinutes(s, d)
      const newDuration = mins > 0 ? formatWorkdayDuration(mins) : ''
      setDuration(newDuration)
      onFieldSave?.('duration', newDuration)
      return
    }
    const mins = parseWorkdayDuration(duration)
    if (mins) {
      const newStart = subtractWorkingMinutes(d, mins).toISOString()
      setStart(newStart)
      onFieldSave?.('start', newStart)
    }
  }

  const handleWhenDurationChange = (v: string) => {
    setDuration(v)
    onFieldSave?.('duration', v)
    const mins = parseWorkdayDuration(v)
    if (!mins) return
    const s = start ? parseDatetimeLocal(start) : null
    if (s) {
      const newDue = addWorkingMinutes(s, mins).toISOString()
      setDue(newDue)
      onFieldSave?.('due', newDue)
      return
    }
    const d = due ? parseDatetimeLocal(due) : null
    if (d) {
      const newStart = subtractWorkingMinutes(d, mins).toISOString()
      setStart(newStart)
      onFieldSave?.('start', newStart)
    }
  }

  // Freeform materials state
  const [freeformMaterials, setFreeformMaterials] = React.useState<FreeformMaterial[]>([])
  const [showMaterialModal, setShowMaterialModal] = React.useState(false)
  const [editingMaterial, setEditingMaterial] = React.useState<FreeformMaterial | null>(null)

  // §3.13's task-level rollup, as the SERVER computed it. Held beside the
  // rows and always replaced together with them (see loadMaterials), so the
  // header and the list are always the same snapshot.
  const [sourcingRisk, setSourcingRisk] = React.useState<SourcingRisk | null>(null)

  // loadMaterials — the ONE place the facet's rows and rollup are fetched.
  //
  // Every material mutation re-runs it instead of patching the local array.
  // The old optimistic merge (`{ ...m, ...data }`) kept only the fields the
  // form submitted, so every SERVER-DERIVED field on the row went stale the
  // moment anyone edited it: change a need-by and the row kept the at-risk
  // pill computed against the OLD date until the peek was reopened. At-risk
  // is derived per read by design (§3.13), so a read is the only thing that
  // can refresh it — there is no client-side copy of the rule to re-run, and
  // adding one would be the second definition this whole epic exists to
  // prevent.
  const loadMaterials = React.useCallback(() => {
    if (!t.id) return
    authedFetch(`/api/v1/tasks/${t.id}/materials`, {}, { redirectOnAuthFailure: false })
      .then(r => r.ok ? r.json() : { materials: [] })
      .then(data => {
        setFreeformMaterials(data.materials || [])
        setSourcingRisk(data.sourcing_risk ?? null)
      })
      .catch(() => {/* silent — not critical */})
  }, [t.id])

  // Fetch freeform materials when task opens
  React.useEffect(() => {
    if (!open || !t.id || kind !== 'task') {
      setFreeformMaterials([])
      setSourcingRisk(null)
      return
    }
    loadMaterials()
  }, [open, t.id, kind, loadMaterials])

  // ── Cost facet committed/actual figures (UOW-3 #3593) ───────────────
  // Real numbers from the shared budget inclusion rules (committed =
  // issued POs/subcontracts not yet billed; actual = approved/paid
  // bills), per task — or rolled up over child tasks for a phase. Values
  // are Postgres aggregates rendered as-is (NUMERIC-safe: no client-side
  // money math beyond formatting). Fetch is gated on the same
  // estimates.view permission that gates the facet; any failure degrades
  // to hidden (never blocks the peek).
  const [costTotals, setCostTotals] = React.useState<TaskCostTotals | null>(null)
  // Bumped whenever the peek itself changes the task↔estimate-line graph, so
  // the cost figures and the linked-lines list refetch together.
  const [costRefreshKey, setCostRefreshKey] = React.useState(0)
  React.useEffect(() => {
    if (!open || !t.id || (kind !== 'task' && kind !== 'phase') || !canViewEstimates) {
      setCostTotals(null)
      return
    }
    let cancelled = false
    authedFetch(`/api/v1/tasks/${t.id}/materials/cost`, {}, { redirectOnAuthFailure: false })
      .then(r => (r.ok ? r.json() : null))
      .then((data: TaskCostTotals | null) => { if (!cancelled) setCostTotals(data) })
      .catch(() => { if (!cancelled) setCostTotals(null) })
    return () => { cancelled = true }
  }, [open, t.id, kind, canViewEstimates, costRefreshKey])

  // ── Related work (UOW-4G #3672 — supersedes the 4F linked-lines list) ─
  // ONE batched fetch per peek open (RelatedWorkSection below owns it) feeds
  // BOTH the card strip and this derived state: how many estimate lines the
  // task holds decides whether the single-value picker is safe to render
  // (pointing it elsewhere would silently collapse a combined task). A fetch
  // failure degrades to the picker (the pre-4F presentation) — the server
  // refuses a collapsing write regardless (§3.10), so this is display-only.
  const [relatedCards, setRelatedCards] = React.useState<RelatedWorkCard[]>([])
  const [removingLineId, setRemovingLineId] = React.useState<number | null>(null)
  // OWNED lines only (no is_secondary "link anyway" references): the picker
  // hides and the per-line remove appears only when the task actually HOLDS
  // several link rows — a secondary neither combines the task nor splits.
  const relatedLineCount = React.useMemo(
    () => relatedCards.filter(c => c.kind === 'estimate_line' && !c.unavailable && !c.is_secondary).length,
    [relatedCards],
  )

  // Remove one line from a combined task: the server unlinks it and gives it
  // its own task back through the co-drafting engine (contract §3.10). The
  // schedule behind the peek has to refetch to show that new task, which is
  // what onEstimateLinksChanged is for.
  const handleRemoveEstimateLine = React.useCallback(async (lineItemId: number) => {
    if (!t.id) return
    setRemovingLineId(lineItemId)
    try {
      const res = await authedFetch(
        `/api/v1/tasks/${t.id}/estimate-lines/${lineItemId}`,
        { method: 'DELETE' },
        { redirectOnAuthFailure: false },
      )
      if (!res.ok) return
      setCostRefreshKey(k => k + 1)
      onEstimateLinksChanged?.()
    } catch {
      /* leave the list as-is; the next open refetches */
    } finally {
      setRemovingLineId(null)
    }
  }, [t.id, onEstimateLinksChanged])

  // ── Stacked linked-doc peek (UOW-1A #3586) ──────────────────────────
  // A material's PO/bid chip opens the real doc peek OVER this task peek
  // (SlideOver layers above SidePeek); closing it lands back here — the
  // same stacking the Procurement Tracker uses when it opens this peek
  // over a tracker item. Rendered as a SIBLING of SidePeek below because
  // the peek panel is CSS-transformed, which would otherwise become the
  // containing block for the doc peek's position:fixed overlay.
  const [linkedDocPeek, setLinkedDocPeek] = React.useState<LinkedDocPeekState | null>(null)
  // Stacking ladder (UOW-4G): doc peeks open one step above THIS peek — and
  // never below the SlideOver default (901) that layers them over the z-200
  // SidePeek — so the chain still stacks when the task peek itself was
  // opened over another SlideOver.
  const docPeekZ = Math.max(901, (zIndex ?? 200) + 10)

  // UOW-5A (§3.12): a sourcing requirement is one click from its tracker
  // actions — deep-link to the Procurement tracker with the row's item id;
  // the tracker opens its own TrackerItemPeek (bid it / order it /
  // subcontract it, §3.3) from ?item=. Same deep-link idiom as the
  // subcontract chip below.
  const handleOpenTracker = React.useCallback((id: number) => {
    window.location.href = `/procurement?tab=tracker&item=${id}`
  }, [])

  const handleOpenLinkedDoc = React.useCallback((docKind: LinkedDocKind, id: number) => {
    if (docKind === 'subcontract') {
      // The material chips carry only the NUMERIC linked_subcontract_id and
      // the /subcontracts-v2 routes resolve public_id — deep-link to the
      // procurement Subcontracts tab, which auto-opens the row's peek from
      // ?highlight=. (Related-work sub cards DO carry public_id and stack
      // the real SubPeek — handleOpenRelatedCard below.)
      window.location.href = `/procurement?tab=subcontracts&highlight=${id}`
      return
    }
    if (docKind === 'po') {
      purchaseOrders.get(String(id))
        .then(po => setLinkedDocPeek({ kind: 'po', po }))
        .catch(() => {/* degrade silently — never block the peek */})
    } else {
      bidRequests.get(id)
        .then(bid => setLinkedDocPeek({ kind: 'bid', bid }))
        .catch(() => {/* degrade silently — never block the peek */})
    }
  }, [])

  // UOW-4G (#3672): related-work card click-through. PO/Bid/Sub cards open
  // their REAL doc peeks stacked over this peek (the promise rejection path
  // lets the strip mark an unfetchable target unopenable instead of
  // re-offering a dead click); estimate-line and bill cards live on their
  // own pages, so both their primary and secondary actions navigate.
  const handleOpenRelatedCard = React.useCallback((card: RelatedWorkCard): void | Promise<void> => {
    switch (card.kind) {
      case 'purchase_order':
        return purchaseOrders.get(card.id).then(po => setLinkedDocPeek({ kind: 'po', po }))
      case 'bid_request':
        return bidRequests.get(Number(card.id)).then(bid => setLinkedDocPeek({ kind: 'bid', bid }))
      case 'subcontract': {
        if (!card.public_id) { openRelatedCardPage(card); return }
        const publicID = card.public_id
        return authedFetch(`/api/v1/subcontracts-v2/${publicID}`, {}, { redirectOnAuthFailure: false })
          .then(res => {
            if (!res.ok) throw new Error('Subcontract not found')
            return res.json()
          })
          .then((sub: import('@/components/procurement/SubPeek').SubcontractV2) => {
            setLinkedDocPeek({ kind: 'sub', sub, changes: [], loadingChanges: true })
            const settleChanges = (changes: import('@/components/procurement/SubPeek').SubcontractChange[]) =>
              setLinkedDocPeek(prev => (prev && prev.kind === 'sub' && prev.sub.id === sub.id)
                ? { ...prev, changes, loadingChanges: false }
                : prev)
            authedFetch(`/api/v1/subcontracts/${publicID}/changes`, {}, { redirectOnAuthFailure: false })
              .then(r => (r.ok ? r.json().catch(() => ({})) : {}))
              .then((ch: { data?: import('@/components/procurement/SubPeek').SubcontractChange[]; items?: import('@/components/procurement/SubPeek').SubcontractChange[] }) =>
                settleChanges(ch?.data ?? ch?.items ?? []))
              .catch(() => settleChanges([]))
          })
      }
      case 'task':
      case 'phase':
        // A task subject never yields task cards, but the shared strip can —
        // route through the host's stacked-task opener when it exists.
        if (onOpenTask) { onOpenTask(card.id); return }
        openRelatedCardPage(card)
        return
      default:
        // estimate_line → the estimate editor; bill → the procurement Bills
        // tab. Neither has a stackable peek from here.
        openRelatedCardPage(card)
        return
    }
  }, [onOpenTask])

  // Close any stacked doc peek when the task changes or the peek closes.
  // (A task switch normally remounts this component via the wrapper's
  // per-task key, but the effect must match its promise even if a caller
  // path ever bypasses that.)
  React.useEffect(() => {
    setLinkedDocPeek(null)
  }, [open, t.id])

  // ESC while a doc peek is stacked must close ONLY the doc peek — the
  // shared shells' unconditional bubble-phase ESC listeners (#2333, still
  // untouched) would otherwise close both layers. Registered with the ONE
  // shared depth registry (lib/peekEscStack): each ESC closes exactly the
  // top layer across every host, however deep the chain goes.
  usePeekEscLayer(!!linkedDocPeek, () => setLinkedDocPeek(null))

  // Variance indicator (UOW-1A): fires when any of this task's material
  // rows differ from THEIR OWN source estimate line (per-material join on
  // the material's estimate_line_item_id — see materialsDifferFromEstimate
  // for why the task's linked line is the wrong key). The lookup reuses
  // the already-fetched project estimate lines — no extra requests.
  // Display-only; gated with the Cost facet.
  const estimateVariance = React.useMemo(() => {
    if (!canViewEstimates || estimateLineItemOptions.length === 0) return false
    const byId = new Map(estimateLineItemOptions.map(o => [o.id, o]))
    return materialsDifferFromEstimate(freeformMaterials, byId)
  }, [canViewEstimates, estimateLineItemOptions, freeformMaterials])

  // Load attachments when the peek opens. The peek owns its attachment list
  // (callers don't feed t.attachments), so fetch directly off the generic
  // /tasks/{id}/attachments route. Re-runs on every open so a freshly-attached
  // file shows after upload.
  const loadAttachments = React.useCallback(async () => {
    if (!t.id) return
    try {
      const r = await authedFetch(`/api/v1/tasks/${t.id}/attachments`, {}, { redirectOnAuthFailure: false })
      if (!r.ok) return
      const data = await r.json()
      const list: any[] = Array.isArray(data) ? data : (data?.attachments ?? data?.data ?? [])
      setAttachments(list.map((a: any) => ({
        id: String(a.id),
        filename: a.original_name || a.filename || 'file',
        url: a.url || a.storage_path || '',
        thumbnail_url: a.thumbnail_url || undefined,
        size: a.file_size ?? a.size,
        mime_type: a.content_type ?? a.mime_type,
        created_at: a.created_at,
      })))
    } catch {/* silent — not critical */}
  }, [t.id])

  React.useEffect(() => {
    if (!open || !t.id) return
    void loadAttachments()
  }, [open, t.id, loadAttachments])

  // Activity thread — CommentPanel (variant="inline") owns the fetch/poll/
  // post lifecycle now (#1536). This peek only needs to force an immediate
  // refetch when something OTHER than a comment post writes a new
  // auto-logged event server-side (flag submit/resolve below) — bumping
  // this counter is that signal.
  const [activityRefreshSignal, setActivityRefreshSignal] = React.useState(0)

  const handleAddMaterial = () => {
    setEditingMaterial(null)
    setShowMaterialModal(true)
  }
  const handleEditMaterial = (m: FreeformMaterial) => {
    setEditingMaterial(m)
    setShowMaterialModal(true)
  }
  const handleDeleteMaterial = async (id: number) => {
    const res = await authedFetch(`/api/v1/task-materials/${id}`, {
      method: 'DELETE',
    }, { redirectOnAuthFailure: false })
    if (res.ok) {
      loadMaterials()
    }
  }
  const handleSaveMaterial = async (data: Omit<FreeformMaterial, 'id' | 'stock_status'>) => {
    if (editingMaterial) {
      const res = await authedFetch(`/api/v1/task-materials/${editingMaterial.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }, { redirectOnAuthFailure: false })
      if (res.ok) {
        loadMaterials()
      }
    } else {
      const res = await authedFetch(`/api/v1/tasks/${t.id}/materials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }, { redirectOnAuthFailure: false })
      if (res.ok) {
        loadMaterials()
      }
    }
    setShowMaterialModal(false)
  }

  // Sync status state machine when kind changes
  React.useEffect(() => {
    if (kind === 'decision' && !['draft', 'pending', 'approved', 'rejected', 'revised'].includes(status)) {
      setStatus('draft')
    } else if (kind === 'milestone' && !['pending', 'hit', 'missed'].includes(status)) {
      setStatus('pending')
    } else if (kind === 'task' && !['not_started', 'in_progress', 'blocked', 'completed', 'on_hold', 'cancelled'].includes(status)) {
      setStatus('not_started')
    } else if (kind === 'phase') {
      setStatus('in_progress')
    }
  }, [kind])

  // Esc-to-close is now handled by SidePeek itself.

  // File input ref for programmatic click
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = React.useState(false)
  const [attachError, setAttachError] = React.useState<string | null>(null)

  // In-app attachment preview (lightbox), shared by PhotosSection and
  // FilesSection so opening ANY attachment — photo or file — never navigates
  // the user out of the app (WEB-ATTACH-01).
  const [preview, setPreview] = React.useState<PeekAttachment | null>(null)
  const openPreview = React.useCallback((a: PeekAttachment) => { setPreview(a) }, [])

  // Upload a file to this task's attachments. Honour a caller-supplied
  // onFileUpload override if present, otherwise POST directly to the generic
  // attachments route. Always refetch so the new file appears immediately
  // (previously onFileUpload was undefined on every caller, so drops/clicks
  // were silent no-ops). Rejections (wrong type, too large) come back as a
  // JSON {error} body — surface that message instead of failing silently.
  // When onFileUpload handles a not-yet-created task (no t.id — WEB-CREATE-01),
  // there's no server row to refetch, so add an optimistic local preview
  // ourselves; loadAttachments() below is a no-op without t.id and would
  // otherwise leave the drop looking silent even though it was buffered.
  const uploadFile = React.useCallback(async (file: File) => {
    if (!canEdit) return
    setAttachError(null)
    if (onFileUpload) {
      const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      await onFileUpload(file, localId)
      if (!t.id) {
        setAttachments(prev => [...prev, {
          id: localId,
          filename: file.name,
          url: URL.createObjectURL(file),
          mime_type: file.type,
          size: file.size,
        }])
      }
    } else if (t.id) {
      const fd = new FormData()
      fd.append('file', file)
      try {
        const res = await authedFetch(`/api/v1/tasks/${t.id}/attachments`, {
          method: 'POST',
          body: fd,
        }, { redirectOnAuthFailure: false })
        if (!res.ok) {
          const body = await res.json().catch(() => null)
          setAttachError(body?.error || `Upload failed (${res.status})`)
        }
      } catch {
        setAttachError('Upload failed — check your connection and try again.')
      }
    }
    await loadAttachments()
  }, [onFileUpload, t.id, loadAttachments])

  const handleRemoveAttachment = React.useCallback(async (attId: string) => {
    if (!canEdit) return
    if (onFileRemove) {
      await onFileRemove(attId)
      if (!t.id) {
        setAttachments(prev => prev.filter(a => a.id !== attId))
      }
    } else if (t.id) {
      try {
        await authedFetch(`/api/v1/tasks/${t.id}/attachments/${attId}`, {
          method: 'DELETE',
        }, { redirectOnAuthFailure: false })
      } catch {/* surfaced via list refresh */}
    }
    await loadAttachments()
  }, [canEdit, onFileRemove, t.id, loadAttachments])

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    await uploadFile(file)
    if (e.target) e.target.value = ''
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    await uploadFile(file)
  }

  // Flag-a-problem (#802/#810) — the peek owns this fetch/state itself,
  // same convention as attachments/comments above (callers don't feed
  // flag data in). Submitting/resolving both come back with the task's
  // new status, which we apply locally instead of re-fetching the peek —
  // the backend already ran the status transition + activity logging
  // server-side, so refetching comments picks up the auto-logged events.
  const flagPeopleOptions: FlagPersonOption[] = assigneeOptions.map(a => ({
    id: a.id, name: a.name, initials: a.initials,
  }))

  const handleFlagSubmit = React.useCallback(async (payload: FlagSubmitPayload) => {
    if (!t.id || flagBusy) return
    setFlagBusy(true)
    try {
      const res = await appTasks.flag(t.id, payload)
      setFlag(res.flag)
      if (res.task?.status) setStatus(API_TO_PEEK_STATUS[res.task.status] ?? res.task.status)
      setFlagOpen(false)
      setActivityRefreshSignal((n) => n + 1)
    } finally {
      setFlagBusy(false)
    }
  }, [t.id, flagBusy])

  const handleFlagResolve = React.useCallback(async () => {
    if (!t.id || resolveBusy) return
    setResolveBusy(true)
    try {
      const res = await appTasks.resolveFlag(t.id)
      setFlag(null)
      if (res.task?.status) setStatus(API_TO_PEEK_STATUS[res.task.status] ?? res.task.status)
      setActivityRefreshSignal((n) => n + 1)
    } finally {
      setResolveBusy(false)
    }
  }, [t.id, resolveBusy])

  // Auto-materials from approved spec deps
  const autoMaterials = kind === 'task'
    ? deps
      .filter((d) => d.type === 'spec' && d.status === 'approved')
      .map((d) => ({ specId: d.id }))
    : []

  const deleteLabel = kind === 'phase' ? 'Delete phase'
    : kind === 'milestone' ? 'Delete milestone'
    : kind === 'decision' ? 'Delete decision'
    : 'Delete task'

  const menuItems = [
    ...(onDuplicate ? [{ label: 'Duplicate', icon: 'layers', onSelect: onDuplicate }] : []),
    { label: 'Copy link', icon: 'link', onSelect: () => {
      if (t.id) navigator.clipboard?.writeText(window.location.origin + `?peek=${t.id}`)
    }},
    ...(onDelete ? [
      { separator: true } as const,
      { label: deleteLabel, icon: 'trash', tone: 'danger' as const, onSelect: onDelete },
    ] : []),
  ]

  // Header title label — "Create task" / "Edit task" / "Task" (task-peek-web.jsx
  // PeekHeader: create={role==='create'} title logic). isCreate = no id yet
  // (mirrors the isCreate flag already used for Files/Activity below).
  const isCreate = !t.id
  const kindLabel = kind === 'task' ? 'task'
    : kind === 'milestone' ? 'milestone'
    : kind === 'decision' ? 'decision'
    : 'phase'
  const headerTitle = canEdit ? (isCreate ? `Create ${kindLabel}` : `Edit ${kindLabel}`) : kindLabel

  const header = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '10px 12px',
      minHeight: 48,
      borderBottom: '1px solid var(--border)',
      flexShrink: 0,
    }}>
      {onBack && (
        <button type="button" onClick={onBack} aria-label="Back" style={iconBtn}
          title={breadcrumb ? `Back to ${breadcrumb}` : 'Back'}>
          <Icon name="chevL" size={15} sw={2} />
        </button>
      )}
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
        letterSpacing: 0.8, textTransform: 'uppercase' as const,
        color: 'var(--text-muted)',
        display: 'flex', alignItems: 'center', gap: 6,
        minWidth: 0, overflow: 'hidden',
      }}>
        {breadcrumb && (
          <>
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              color: 'var(--text-dim)', fontWeight: 600, maxWidth: 140,
            }}>{breadcrumb}</span>
            <Icon name="chevR" size={9} sw={2} style={{ color: 'var(--text-dim)' }} />
          </>
        )}
        <span>{headerTitle}</span>
      </div>
      <div style={{ flex: 1 }} />
      {/* Flag action — header placement per task-peek-web.jsx (Flag → header
          toolbar, not a mid-panel row). Flags only apply to tasks (task_flags
          FK's to app_tasks) and need a saved task id. */}
      {canEdit && kind === 'task' && !isCreate && (
        <button type="button" onClick={() => setFlagOpen(true)} aria-label="Flag a problem"
          title={flag ? 'Task is flagged' : 'Flag a problem'}
          style={{
            ...iconBtn,
            color: flag ? 'var(--st-error)' : 'var(--text-muted)',
            background: flag ? 'color-mix(in srgb, var(--st-error) 12%, transparent)' : 'transparent',
          }}>
          <Icon name="flag" size={14} sw={1.9} />
        </button>
      )}
      {canEdit && menuItems.length > 0 && (
        <DropdownMenu
          side="bottom" align="end" width={180}
          trigger={
            <button type="button" aria-label="More actions" style={iconBtn}>
              <Icon name="more" size={14} sw={2} />
            </button>
          }
          items={menuItems} />
      )}
      {onExpand && (
        <button type="button" onClick={onExpand} aria-label="Expand" style={iconBtn}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 1h4v4M5 13H1V9M13 1l-5 5M1 13l5-5"
              stroke="currentColor" strokeWidth="1.6"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
      <button type="button" onClick={onClose} aria-label="Close" style={iconBtn}>
        <Icon name="x" size={15} sw={2} />
      </button>
    </div>
  )

  return (
    <>
    <SidePeek open={open} onClose={onClose} width={mobile ? undefined : 440} anchor={mobile ? 'fullscreen' : 'fixed'} closeOnOutsideClick={false}
      zIndex={zIndex}>
      {header}
      <div
        style={{ flex: 1, overflowY: 'auto' }}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}>
        <TitleBlock
          kind={kind} kindList={kindList} name={name} taskId={t.id}
          priority={priority} status={status}
          start={kind === 'task' ? start : null}
          due={kind === 'milestone' ? milestoneDate : kind === 'decision' ? dueBy : due}
          notes={notes}
          onNotesChange={(v) => { setNotes(v); onFieldSave?.('notes', v) }}
          notesEditable={fieldEditable('notes')}
          canEdit={canEdit}
          canEditField={canEditField}
          canEditStatus={fieldEditable('status')}
          onKindChange={handleKindChange}
          onNameChange={(v) => { setName(v); onFieldSave?.('name', v) }}
          onPriorityChange={(v) => { setPriority(v); onFieldSave?.('priority', v) }}
          onStatusChange={(v) => { setStatus(v); onFieldSave?.('status', v) }}
          flagBanner={kind === 'task' && flag ? (
            <TaskFlagBanner
              flag={flag} attachments={attachments} peopleOptions={flagPeopleOptions}
              canEdit={canEdit} onResolve={handleFlagResolve} resolving={resolveBusy} />
          ) : null}
        />

        <PropertiesSection kind={kind} taskId={t.id}
          assignees={assignees} decider={decider} assigneeNames={t.assigneeNames}
          onAssigneesChange={(v) => { setAssignees(v); onFieldSave?.('assignees', v) }}
          onDeciderChange={(v) => { setDecider(v); onFieldSave?.('decider', v) }}
          assigneeOptions={assigneeOptions}
          project={project} phase={phase} room={room}
          fsRoom={fsRoom} fsRoomName={fsRoomName}
          projectName={t.projectName} roomName={t.roomName} phaseName={t.phaseName}
          onProjectChange={(v) => {
            setProject(v)
            if (!v) {
              // Project-scoped values cannot survive an explicit return to an
              // unscoped task. Clear the whole dependent Where/parent context
              // so the submitted draft cannot retain hidden stale IDs.
              setPhase(null)
              setRoom(null)
              setFsRoom(null)
              setFsRoomName(null)
              setParentTask(null)
              onFieldSave?.('phase', null)
              onFieldSave?.('room', null)
              onFieldSave?.('room_folder_id', null)
              onFieldSave?.('parentTask', null)
              onFieldSave?.('project', null)
              return
            }
            // Prefill Location from the newly-selected project's own location
            // (WHERE-LOCATION-01 AC1) — only when no Location is already set,
            // so a manually-chosen Location survives a later project change.
            if (!room) {
              const proj = projectOptions.find(p => p.id === v)
              if (proj?.locationId != null) {
                const locId = String(proj.locationId)
                setRoom(locId)
                onFieldSave?.('room', locId)
              }
            }
            onFieldSave?.('project', v)
          }}
          onPhaseChange={(v) => { setPhase(v); onFieldSave?.('phase', v) }}
          onLocationRoomChange={(v) => {
            // Picking a Room sets both roomId and its parent locationId;
            // picking a bare Location (or clearing) always clears the room —
            // there's only one row now, so no partial-mismatch check needed.
            setRoom(v.locationId)
            setFsRoom(v.roomId)
            setFsRoomName(v.roomName ?? null)
            onFieldSave?.('room', v.locationId)
            onFieldSave?.('room_folder_id', v.roomId)
          }}
          phases={phases} locations={locations}
          fsRoomOptions={fsRoomOptions}
          projectOptions={projectOptions}
          start={start} due={due} duration={duration}
          dueBy={dueBy} milestoneDate={milestoneDate} orderLeadDate={orderLead}
          hasLinkedSpec={!!linkedSpec}
          onStartChange={handleWhenStartChange}
          onDueChange={handleWhenDueChange}
          onDurationChange={handleWhenDurationChange}
          onDueByChange={(v) => { setDueBy(v); onFieldSave?.('dueBy', v) }}
          onMilestoneDateChange={(v) => { setMilestoneDate(v); onFieldSave?.('milestoneDate', v) }}
          onOrderLeadChange={(v) => { setOrderLead(v); onFieldSave?.('orderLead', v) }}
          parentTask={parentTask} goal={goal} repeat={repeat}
          billable={billable} equipment={equipment}
          taskType={taskType}
          onParentChange={(v) => { setParentTask(v); onFieldSave?.('parentTask', v) }}
          onGoalChange={(v) => { setGoal(v); onFieldSave?.('goal', v) }}
          onRepeatChange={(v) => { setRepeat(v); onFieldSave?.('repeat', v) }}
          onBillableChange={(v) => { setBillable(v); onFieldSave?.('billable', v) }}
          onEquipmentChange={(v) => { setEquipment(v); onFieldSave?.('equipment', v) }}
          onTaskTypeChange={(v) => { setTaskType(v); onFieldSave?.('taskType', v) }}
          canEdit={canEdit} canEditTaskType={fieldEditable('taskType')} taskTypeEnabled={taskTypeEnabled}
          goalOptions={goalOptions} equipmentOptions={equipmentOptions}
          taskTypeOptions={effectiveTypeOptions}
          parentTaskOptions={parentTaskOptions} />

        {kind === 'task' && (
          <ChildTasksSection childTasks={childTasks} onOpenChildTask={onOpenChildTask || onOpenTask} />
        )}

        {/* Cost facet (UOW-1A): section entirely absent without estimate
            view permission — locked decision, never disabled/greyed. */}
        {/* UOW-4F (#3671) via UOW-4G (#3672): a COMBINED task (more than one
            linked estimate line) hides the single-value picker — pointing it
            elsewhere would silently collapse the combine — and the lines
            render as removable cards in the Related work strip below.
            One line or none → the picker is unchanged. */}
        {(kind === 'task' || kind === 'phase') && canViewEstimates && !(kind === 'task' && relatedLineCount > 1) && (
          <EstimateLineItemSection
            linkedItem={linkedEstimateLineItem}
            options={estimateLineItemOptions}
            onChange={(v) => { setEstimateLineItemId(v ? String(v.id) : null); onFieldSave?.('estimate_line_item_id', v?.id ?? null) }}
            onCreateNew={handleCreateEstimateLineItem}
            canEdit={canEdit}
            variance={estimateVariance}
            costTotals={costTotals}
            isPhase={kind === 'phase'}
          />
        )}

        {/* Sub confirmations (UOW-5C #3791, contract §3.14): the handshake
            state on the facet — who was asked, the window, pending/confirmed/
            declined/stale — plus the thin send affordance. Self-fetching;
            degrades to hidden on failure and swallows itself when empty for
            a viewer who can't send (facet rule: never block the peek). */}
        {(kind === 'task' || kind === 'phase') && open && t.id && (
          <SubConfirmationsSection
            taskId={t.id}
            canSend={canEdit ?? true}
            renderSection={(content) => (
              <section style={sectStyle}>
                <SectionLabel>Sub confirmations</SectionLabel>
                {content}
              </section>
            )}
          />
        )}

        {/* Related work (UOW-4G #3672, contract §3.11): every linked
            schedule / estimate / sourcing record as cards — one batched
            fetch, absence renders nothing (the section swallows itself).
            Estimate-line cards carry the 4F per-line remove (split) when
            the task holds several lines. */}
        {(kind === 'task' || kind === 'phase') && (
          <RelatedWorkSection
            subjectType="task"
            subjectId={open && t.id ? t.id : null}
            refreshKey={costRefreshKey}
            onCards={setRelatedCards}
            onOpenCard={handleOpenRelatedCard}
            onRemoveLine={canEdit && kind === 'task' ? handleRemoveEstimateLine : undefined}
            removingLineId={removingLineId}
            showEstimateBaseline
            renderSection={(content) => (
              <section style={sectStyle}>
                <SectionLabel>Related work</SectionLabel>
                {content}
              </section>
            )}
          />
        )}

        {kind === 'decision' && (
          <DecisionSettingsSection
            subKind={subKind} selectionMode={selectionMode}
            closeMode={closeMode} hasPricing={hasPricing}
            onSubKindChange={(v) => { setSubKind(v); onFieldSave?.('subKind', v) }}
            onSelectionModeChange={(v) => { setSelectionMode(v); onFieldSave?.('selectionMode', v) }}
            onCloseModeChange={(v) => { setCloseMode(v); onFieldSave?.('closeMode', v) }}
            onHasPricingChange={(v) => { setHasPricing(v); onFieldSave?.('hasPricing', v) }}
            canEdit={canEdit} />
        )}

        {kind === 'decision' && (
          <FSBridgeSection linkedSpec={linkedSpec}
            onLinkedSpecChange={(v) => { setLinkedSpec(v); onFieldSave?.('linkedSpec', v) }}
            canEdit={canEdit} specOptions={specOptions} />
        )}

        {kind === 'decision' && (subKind === 'selection' || viewMode === 'client_review') && (
          <OptionsSection options={options}
            onOptionsChange={(v) => { setOptions(v); onFieldSave?.('options', v) }}
            canEdit={canEdit} hasPricing={hasPricing}
            mode={selectionMode as 'single' | 'multiple'}
            clientReview={viewMode === 'client_review'}
            onOptionSelect={onOptionSelect} />
        )}

        <DependenciesSection kind={kind} deps={deps}
          onDepsChange={(v) => { setDeps(v); onFieldSave?.('dependencies', v) }}
          canEdit={canEdit} onOpenSpec={onOpenLinkedSpec} onOpenTask={onOpenTask}
          depItems={effectiveDepItems} blockingTasks={blockingTasks}
          onRemoveDependency={onRemoveDependency} />

        <PhotosSection
          attachments={attachments}
          onAttachFile={() => fileInputRef.current?.click()}
          onRemoveAttachment={handleRemoveAttachment}
          canEdit={canEdit} openPreview={openPreview} />

        {attachError && (
          <div style={{
            margin: '-4px 0 12px', padding: '7px 10px',
            background: 'color-mix(in srgb, var(--st-error) 10%, transparent)',
            border: '1px solid color-mix(in srgb, var(--st-error) 35%, transparent)',
            borderRadius: 'var(--r-sm)',
            fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--st-error)',
          }}>{attachError}</div>
        )}

        <FilesSection kind={kind}
          materials={[]} attachments={attachments}
          autoMaterials={autoMaterials}
          freeformMaterials={freeformMaterials}
          sourcingRisk={sourcingRisk}
          onAttachFile={() => fileInputRef.current?.click()}
          onOpenSpec={onOpenLinkedSpec}
          canEdit={canEdit}
          onAddMaterial={handleAddMaterial}
          onEditMaterial={handleEditMaterial}
          onDeleteMaterial={handleDeleteMaterial}
          onRemoveAttachment={handleRemoveAttachment}
          openPreview={openPreview}
          onOpenLinkedDoc={handleOpenLinkedDoc}
          onOpenTracker={handleOpenTracker}
          materialStatus={t.materialStatus} />

        <AttachmentViewer attachment={preview ? toViewerAttachment(preview) : null}
          onClose={() => setPreview(null)} />

        {kind !== 'phase' && (
          <ActivitySection
            taskId={t.id}
            canEdit={canEdit}
            isCreate={!t.id}
            refreshSignal={activityRefreshSignal}
          />
        )}

        {/* Free-form material modal */}
        <AddEditMaterialModal
          open={showMaterialModal}
          onClose={() => setShowMaterialModal(false)}
          onSave={handleSaveMaterial}
          initial={editingMaterial}
        />

        {/* Drag-over overlay */}
        {isDragOver && canEdit && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'color-mix(in srgb, var(--brand-gold) 8%, transparent)',
            border: '2px dashed var(--brand-gold)',
            borderRadius: 'var(--r-md)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none', zIndex: 10,
          }}>
            <span style={{
              fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: 600,
              color: 'var(--brand-gold)',
            }}>Drop to attach</span>
          </div>
        )}
      </div>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.heic,.webp,.pdf,.docx,.xlsx,.csv,.txt"
        style={{ display: 'none' }}
        onChange={handleFileInput}
      />
      {/* Optional sticky footer (e.g. create-mode CTA) */}
      {footer && (
        <div style={{
          flexShrink: 0,
          padding: '10px 16px',
          paddingBottom: mobile ? 'calc(10px + env(safe-area-inset-bottom, 0px))' : '10px',
          borderTop: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'var(--bg-surface)',
        }}>
          {footer}
        </div>
      )}
      {kind === 'task' && (
        <FlagDialog
          open={flagOpen}
          onClose={() => setFlagOpen(false)}
          onSubmit={handleFlagSubmit}
          submitting={flagBusy}
          attachments={attachments.filter(isImageAttachment).map(a => ({ id: a.id, url: a.url, thumbnail_url: a.thumbnail_url }))}
          onAttachFile={() => fileInputRef.current?.click()}
          peopleOptions={flagPeopleOptions}
        />
      )}
    </SidePeek>
    {/* Stacked linked-doc peek (UOW-1A / UOW-4G) — sibling of SidePeek
        because the peek panel is CSS-transformed (a transformed ancestor
        becomes the containing block for the doc peek's position:fixed
        overlay). The SlideOver-based doc peeks layer above this peek: one
        z step up from wherever THIS peek sits, so the chain still stacks
        when the task peek itself is raised over another SlideOver. Closing
        one returns to this still-open task peek. */}
    {linkedDocPeek && (
      <React.Suspense fallback={null}>
        {linkedDocPeek.kind === 'po' ? (
          <POPeekLazy po={linkedDocPeek.po} onClose={() => setLinkedDocPeek(null)} zIndex={docPeekZ} />
        ) : linkedDocPeek.kind === 'bid' ? (
          <BidPeekLazy
            bid={linkedDocPeek.bid}
            onClose={() => setLinkedDocPeek(null)}
            onAward={() => {
              // Awarding belongs to the full bid surface (AwardModal wiring
              // lives there) — hand off with the row highlighted.
              window.location.href = `/procurement?tab=bid-requests&highlight=${linkedDocPeek.bid.id}`
            }}
            zIndex={docPeekZ}
          />
        ) : (
          <SubPeekLazy
            sub={linkedDocPeek.sub}
            onClose={() => setLinkedDocPeek(null)}
            onStatusChange={(updated) => setLinkedDocPeek(prev =>
              prev && prev.kind === 'sub' ? { ...prev, sub: updated } : prev)}
            changes={linkedDocPeek.changes}
            loadingChanges={linkedDocPeek.loadingChanges}
            zIndex={docPeekZ}
          />
        )}
      </React.Suspense>
    )}
    </>
  )
}

// ─── Re-export picker types for consumers ─────────────────────────────
export type { PhaseOption, AssigneeOption, EquipmentOption, GoalOption }
export type { LocationOption, DepItem, Dependency, SpecOption, DecisionOption }
